// hack to fix JSON type error "Type instantiation is excessively deep and possibly infinite"
// see: https://github.com/supabase/supabase-js/issues/808
export type Json<D extends number = 9, DA extends unknown[] = []> =
  | string
  | number
  | boolean
  | null
  | (D extends DA["length"] ? unknown : { [key: string]: Json<D, [0, ...DA]> | undefined })
  | (D extends DA["length"] ? unknown : Json<D, [0, ...DA]>[]);

export type Database = {
  graphql_public: {
    Tables: {
      [_ in never]: never;
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      graphql: {
        Args: {
          operationName?: string;
          query?: string;
          variables?: Json;
          extensions?: Json;
        };
        Returns: Json;
      };
    };
    Enums: {
      [_ in never]: never;
    };
    CompositeTypes: {
      [_ in never]: never;
    };
  };
  public: {
    Tables: {
      case_files: {
        Row: {
          bucket_id: string;
          case_id: string;
          created_at: string;
          description: string;
          id: string;
          path: string;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          bucket_id: string;
          case_id: string;
          created_at?: string;
          description?: string;
          id?: string;
          path: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          bucket_id?: string;
          case_id?: string;
          created_at?: string;
          description?: string;
          id?: string;
          path?: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "case_files_case_id_fkey";
            columns: ["case_id"];
            isOneToOne: false;
            referencedRelation: "cases";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "case_files_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      case_tags: {
        Row: {
          created_at: string;
          description: string | null;
          id: string;
          name: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string;
          description?: string | null;
          id?: string;
          name: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string;
          description?: string | null;
          id?: string;
          name?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "case_tags_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "case_tags_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      cases: {
        Row: {
          assignee_id: string | null;
          child_maintenance_id: string | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          event_finished_at: string | null;
          event_started_at: string | null;
          id: string;
          name: string;
          parent_maintenance_id: string | null;
          proposal: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          solution: string | null;
          status: Database["public"]["Enums"]["case_status"];
          type: Database["public"]["Enums"]["case_type"] | null;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
        Insert: {
          assignee_id?: string | null;
          child_maintenance_id?: string | null;
          created_at?: string;
          deployment_id: string;
          description?: string | null;
          device_id?: string | null;
          event_finished_at?: string | null;
          event_started_at?: string | null;
          id?: string;
          name: string;
          parent_maintenance_id?: string | null;
          proposal?: string | null;
          public_id?: string;
          resolution_time?: number | null;
          resolved_at?: string | null;
          serial_id?: number;
          serial_id_prefix?: string;
          solution?: string | null;
          status?: Database["public"]["Enums"]["case_status"];
          type?: Database["public"]["Enums"]["case_type"] | null;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          assignee_id?: string | null;
          child_maintenance_id?: string | null;
          created_at?: string;
          deployment_id?: string;
          description?: string | null;
          device_id?: string | null;
          event_finished_at?: string | null;
          event_started_at?: string | null;
          id?: string;
          name?: string;
          parent_maintenance_id?: string | null;
          proposal?: string | null;
          public_id?: string;
          resolution_time?: number | null;
          resolved_at?: string | null;
          serial_id?: number;
          serial_id_prefix?: string;
          solution?: string | null;
          status?: Database["public"]["Enums"]["case_status"];
          type?: Database["public"]["Enums"]["case_type"] | null;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "cases_assignee_id_fkey";
            columns: ["assignee_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_child_maintenance_id_fkey";
            columns: ["child_maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_parent_maintenance_id_fkey";
            columns: ["parent_maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      cases_history: {
        Row: {
          case_id: string | null;
          created_at: string | null;
          deployment_id: string | null;
          id: string;
          new_val: Json | null;
          old_val: Json | null;
          operation: Database["public"]["Enums"]["db_operation"] | null;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          case_id?: string | null;
          created_at?: string | null;
          deployment_id?: string | null;
          id?: string;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          case_id?: string | null;
          created_at?: string | null;
          deployment_id?: string | null;
          id?: string;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "cases_history_case_id_fkey";
            columns: ["case_id"];
            isOneToOne: false;
            referencedRelation: "cases";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_history_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_history_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      cases_to_case_tags: {
        Row: {
          case_id: string;
          case_tag_id: string;
          created_at: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          case_id: string;
          case_tag_id: string;
          created_at?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          case_id?: string;
          case_tag_id?: string;
          created_at?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "cases_to_case_tags_case_id_fkey";
            columns: ["case_id"];
            isOneToOne: false;
            referencedRelation: "cases";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_case_tags_case_tag_id_fkey";
            columns: ["case_tag_id"];
            isOneToOne: false;
            referencedRelation: "case_tags";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_case_tags_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      cases_to_case_tags_history: {
        Row: {
          case_id: string | null;
          case_tag_id: string | null;
          created_at: string | null;
          id: string;
          new_val: Json | null;
          old_val: Json | null;
          operation: Database["public"]["Enums"]["db_operation"] | null;
          user_id: string | null;
        };
        Insert: {
          case_id?: string | null;
          case_tag_id?: string | null;
          created_at?: string | null;
          id?: string;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
        };
        Update: {
          case_id?: string | null;
          case_tag_id?: string | null;
          created_at?: string | null;
          id?: string;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "cases_to_case_tags_history_case_id_fkey";
            columns: ["case_id"];
            isOneToOne: false;
            referencedRelation: "cases";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_case_tags_history_case_tag_id_fkey";
            columns: ["case_tag_id"];
            isOneToOne: false;
            referencedRelation: "case_tags";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_case_tags_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      cases_to_parts: {
        Row: {
          case_id: string;
          created_at: string;
          id: string;
          part_category_id: string | null;
          part_id: string | null;
          part_model_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          case_id: string;
          created_at?: string;
          id?: string;
          part_category_id?: string | null;
          part_id?: string | null;
          part_model_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          case_id?: string;
          created_at?: string;
          id?: string;
          part_category_id?: string | null;
          part_id?: string | null;
          part_model_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "cases_to_parts_case_id_fkey";
            columns: ["case_id"];
            isOneToOne: false;
            referencedRelation: "cases";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_parts_part_category_id_fkey";
            columns: ["part_category_id"];
            isOneToOne: false;
            referencedRelation: "part_categories";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_parts_part_id_fkey";
            columns: ["part_id"];
            isOneToOne: false;
            referencedRelation: "parts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_parts_part_model_id_fkey";
            columns: ["part_model_id"];
            isOneToOne: false;
            referencedRelation: "part_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_parts_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      cases_to_parts_history: {
        Row: {
          case_id: string | null;
          created_at: string | null;
          id: string;
          new_val: Json | null;
          old_val: Json | null;
          operation: Database["public"]["Enums"]["db_operation"] | null;
          part_category_id: string | null;
          part_id: string | null;
          part_model_id: string | null;
          user_id: string | null;
        };
        Insert: {
          case_id?: string | null;
          created_at?: string | null;
          id?: string;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          part_category_id?: string | null;
          part_id?: string | null;
          part_model_id?: string | null;
          user_id?: string | null;
        };
        Update: {
          case_id?: string | null;
          created_at?: string | null;
          id?: string;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          part_category_id?: string | null;
          part_id?: string | null;
          part_model_id?: string | null;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "cases_to_parts_history_case_id_fkey";
            columns: ["case_id"];
            isOneToOne: false;
            referencedRelation: "cases";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_parts_history_part_category_id_fkey";
            columns: ["part_category_id"];
            isOneToOne: false;
            referencedRelation: "part_categories";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_parts_history_part_id_fkey";
            columns: ["part_id"];
            isOneToOne: false;
            referencedRelation: "parts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_parts_history_part_model_id_fkey";
            columns: ["part_model_id"];
            isOneToOne: false;
            referencedRelation: "part_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "cases_to_parts_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      checklist_template_item_files: {
        Row: {
          bucket_id: string;
          checklist_template_item_id: string;
          created_at: string;
          description: string;
          id: string;
          is_deleted: boolean | null;
          ordering: number;
          path: string;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          bucket_id: string;
          checklist_template_item_id: string;
          created_at?: string;
          description: string;
          id?: string;
          is_deleted?: boolean | null;
          ordering?: number;
          path: string;
          title: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          bucket_id?: string;
          checklist_template_item_id?: string;
          created_at?: string;
          description?: string;
          id?: string;
          is_deleted?: boolean | null;
          ordering?: number;
          path?: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "checklist_template_item_files_checklist_template_item_id_fkey";
            columns: ["checklist_template_item_id"];
            isOneToOne: false;
            referencedRelation: "checklist_template_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_template_item_files_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      checklist_template_items: {
        Row: {
          checklist_template_id: string;
          created_at: string;
          description: string;
          id: string;
          is_deleted: boolean | null;
          ordering: number;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          checklist_template_id: string;
          created_at?: string;
          description: string;
          id?: string;
          is_deleted?: boolean | null;
          ordering?: number;
          title: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          checklist_template_id?: string;
          created_at?: string;
          description?: string;
          id?: string;
          is_deleted?: boolean | null;
          ordering?: number;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "checklist_template_items_checklist_template_id_fkey";
            columns: ["checklist_template_id"];
            isOneToOne: false;
            referencedRelation: "checklist_templates";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_template_items_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      checklist_template_items_to_parts: {
        Row: {
          action: Database["public"]["Enums"]["part_usage_type"];
          checklist_template_item_id: string;
          created_at: string;
          id: string;
          part_category_id: string | null;
          part_id: string | null;
          part_model_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          action?: Database["public"]["Enums"]["part_usage_type"];
          checklist_template_item_id: string;
          created_at?: string;
          id?: string;
          part_category_id?: string | null;
          part_id?: string | null;
          part_model_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          action?: Database["public"]["Enums"]["part_usage_type"];
          checklist_template_item_id?: string;
          created_at?: string;
          id?: string;
          part_category_id?: string | null;
          part_id?: string | null;
          part_model_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "checklist_template_items_to_par_checklist_template_item_id_fkey";
            columns: ["checklist_template_item_id"];
            isOneToOne: false;
            referencedRelation: "checklist_template_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_template_items_to_parts_part_category_id_fkey";
            columns: ["part_category_id"];
            isOneToOne: false;
            referencedRelation: "part_categories";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_template_items_to_parts_part_id_fkey";
            columns: ["part_id"];
            isOneToOne: false;
            referencedRelation: "parts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_template_items_to_parts_part_model_id_fkey";
            columns: ["part_model_id"];
            isOneToOne: false;
            referencedRelation: "part_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_template_items_to_parts_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      checklist_templates: {
        Row: {
          created_at: string;
          description: string;
          id: string;
          is_deleted: boolean | null;
          is_public: boolean | null;
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
        Insert: {
          created_at?: string;
          description: string;
          id?: string;
          is_deleted?: boolean | null;
          is_public?: boolean | null;
          title: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          created_at?: string;
          description?: string;
          id?: string;
          is_deleted?: boolean | null;
          is_public?: boolean | null;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "checklist_templates_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_templates_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      checklist_templates_to_workspaces: {
        Row: {
          checklist_template_id: string;
          created_at: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
        Insert: {
          checklist_template_id: string;
          created_at?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          checklist_template_id?: string;
          created_at?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "checklist_templates_to_workspaces_checklist_template_id_fkey";
            columns: ["checklist_template_id"];
            isOneToOne: false;
            referencedRelation: "checklist_templates";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_templates_to_workspaces_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "checklist_templates_to_workspaces_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      chunked_documents: {
        Row: {
          bbox: number[] | null;
          chunk_number: number | null;
          content: string | null;
          created_at: string;
          document_id: string | null;
          embedding: string | null;
          fts: unknown | null;
          headings: string[] | null;
          id: string;
          label: string | null;
          original_content: string | null;
          page_number: number | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          bbox?: number[] | null;
          chunk_number?: number | null;
          content?: string | null;
          created_at?: string;
          document_id?: string | null;
          embedding?: string | null;
          fts?: unknown | null;
          headings?: string[] | null;
          id: string;
          label?: string | null;
          original_content?: string | null;
          page_number?: number | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          bbox?: number[] | null;
          chunk_number?: number | null;
          content?: string | null;
          created_at?: string;
          document_id?: string | null;
          embedding?: string | null;
          fts?: unknown | null;
          headings?: string[] | null;
          id?: string;
          label?: string | null;
          original_content?: string | null;
          page_number?: number | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "chunked_documents_document_id_fkey";
            columns: ["document_id"];
            isOneToOne: false;
            referencedRelation: "documents";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "chunked_documents_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      connections: {
        Row: {
          created_at: string;
          customer: string;
          id: string;
          msg_time: string | null;
          status: Database["public"]["Enums"]["connection_status"];
          updated_at: string;
          workspace_id: string;
        };
        Insert: {
          created_at?: string;
          customer: string;
          id: string;
          msg_time?: string | null;
          status?: Database["public"]["Enums"]["connection_status"];
          updated_at?: string;
          workspace_id: string;
        };
        Update: {
          created_at?: string;
          customer?: string;
          id?: string;
          msg_time?: string | null;
          status?: Database["public"]["Enums"]["connection_status"];
          updated_at?: string;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "connections_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      connections_to_devices: {
        Row: {
          connection_id: string;
          created_at: string;
          customer: string;
          device_id: string;
          device_serial: string;
          msg_time: string | null;
          status: Database["public"]["Enums"]["connection_status"];
          updated_at: string;
        };
        Insert: {
          connection_id: string;
          created_at?: string;
          customer: string;
          device_id: string;
          device_serial: string;
          msg_time?: string | null;
          status: Database["public"]["Enums"]["connection_status"];
          updated_at?: string;
        };
        Update: {
          connection_id?: string;
          created_at?: string;
          customer?: string;
          device_id?: string;
          device_serial?: string;
          msg_time?: string | null;
          status?: Database["public"]["Enums"]["connection_status"];
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "connections_to_devices_connection_id_customer_fkey";
            columns: ["connection_id", "customer"];
            isOneToOne: false;
            referencedRelation: "connections";
            referencedColumns: ["id", "customer"];
          },
          {
            foreignKeyName: "connections_to_devices_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
        ];
      };
      deployment_invitations: {
        Row: {
          created_at: string;
          deployment_id: string;
          deployment_name: string;
          id: string;
          share_code: string;
          updated_at: string;
          user_id: string | null;
          user_name: string;
          workspace_id: string;
          workspace_name: string;
        };
        Insert: {
          created_at?: string;
          deployment_id: string;
          deployment_name: string;
          id?: string;
          share_code: string;
          updated_at?: string;
          user_id?: string | null;
          user_name: string;
          workspace_id: string;
          workspace_name: string;
        };
        Update: {
          created_at?: string;
          deployment_id?: string;
          deployment_name?: string;
          id?: string;
          share_code?: string;
          updated_at?: string;
          user_id?: string | null;
          user_name?: string;
          workspace_id?: string;
          workspace_name?: string;
        };
        Relationships: [
          {
            foreignKeyName: "deployment_invitations_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "deployment_invitations_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "deployment_invitations_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      deployment_settings: {
        Row: {
          created_at: string;
          default_case_assignee: string | null;
          default_case_notification_email: string | null;
          default_case_notification_email_locale: string;
          default_maintenance_assignee: string | null;
          default_maintenance_notification_email: string | null;
          default_maintenance_notification_email_locale: string;
          deployment_id: string;
          id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          default_case_assignee?: string | null;
          default_case_notification_email?: string | null;
          default_case_notification_email_locale?: string;
          default_maintenance_assignee?: string | null;
          default_maintenance_notification_email?: string | null;
          default_maintenance_notification_email_locale?: string;
          deployment_id: string;
          id?: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          default_case_assignee?: string | null;
          default_case_notification_email?: string | null;
          default_case_notification_email_locale?: string;
          default_maintenance_assignee?: string | null;
          default_maintenance_notification_email?: string | null;
          default_maintenance_notification_email_locale?: string;
          deployment_id?: string;
          id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "deployment_settings_default_case_assignee_fkey";
            columns: ["default_case_assignee"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "deployment_settings_default_maintenance_assignee_fkey";
            columns: ["default_maintenance_assignee"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "deployment_settings_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: true;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
        ];
      };
      deployments: {
        Row: {
          address_1: string | null;
          address_2: string | null;
          city: string | null;
          country: string | null;
          created_at: string;
          deleted_at: string | null;
          id: string;
          name: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
          zipcode: string | null;
        };
        Insert: {
          address_1?: string | null;
          address_2?: string | null;
          city?: string | null;
          country?: string | null;
          created_at?: string;
          deleted_at?: string | null;
          id?: string;
          name: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
          zipcode?: string | null;
        };
        Update: {
          address_1?: string | null;
          address_2?: string | null;
          city?: string | null;
          country?: string | null;
          created_at?: string;
          deleted_at?: string | null;
          id?: string;
          name?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
          zipcode?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "deployments_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "deployments_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      deployments_to_workspaces: {
        Row: {
          created_at: string;
          deployment_id: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
          workspace_name: string;
        };
        Insert: {
          created_at?: string;
          deployment_id: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
          workspace_name: string;
        };
        Update: {
          created_at?: string;
          deployment_id?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
          workspace_name?: string;
        };
        Relationships: [
          {
            foreignKeyName: "deployments_to_workspaces_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "deployments_to_workspaces_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "deployments_to_workspaces_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      device_errors: {
        Row: {
          code: string;
          component: string | null;
          created_at: string;
          description: string | null;
          device_id: string;
          id: number;
          resolved_at: string | null;
          severity: number | null;
          started_at: string;
          title: string;
          updated_at: string;
          x: number | null;
          y: number | null;
          z: number | null;
        };
        Insert: {
          code: string;
          component?: string | null;
          created_at?: string;
          description?: string | null;
          device_id: string;
          id?: number;
          resolved_at?: string | null;
          severity?: number | null;
          started_at: string;
          title: string;
          updated_at?: string;
          x?: number | null;
          y?: number | null;
          z?: number | null;
        };
        Update: {
          code?: string;
          component?: string | null;
          created_at?: string;
          description?: string | null;
          device_id?: string;
          id?: number;
          resolved_at?: string | null;
          severity?: number | null;
          started_at?: string;
          title?: string;
          updated_at?: string;
          x?: number | null;
          y?: number | null;
          z?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "device_errors_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
        ];
      };
      device_files: {
        Row: {
          bucket_id: string;
          created_at: string;
          description: string;
          device_id: string;
          id: string;
          path: string;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          bucket_id: string;
          created_at?: string;
          description?: string;
          device_id: string;
          id?: string;
          path: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          bucket_id?: string;
          created_at?: string;
          description?: string;
          device_id?: string;
          id?: string;
          path?: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "device_files_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_files_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      device_insights_charts: {
        Row: {
          created_at: string;
          datafields: string[];
          deployment_id: string;
          device_id: string | null;
          id: string;
          interval: number;
          name: string;
          type: Database["public"]["Enums"]["chart_type"];
          unit_id: string | null;
          updated_at: string;
          user_id: string | null;
          value_aggregation: Database["public"]["Enums"]["chart_value_aggregation"];
          workspace_id: string;
        };
        Insert: {
          created_at?: string;
          datafields?: string[];
          deployment_id: string;
          device_id?: string | null;
          id?: string;
          interval?: number;
          name: string;
          type?: Database["public"]["Enums"]["chart_type"];
          unit_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
          value_aggregation?: Database["public"]["Enums"]["chart_value_aggregation"];
          workspace_id: string;
        };
        Update: {
          created_at?: string;
          datafields?: string[];
          deployment_id?: string;
          device_id?: string | null;
          id?: string;
          interval?: number;
          name?: string;
          type?: Database["public"]["Enums"]["chart_type"];
          unit_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
          value_aggregation?: Database["public"]["Enums"]["chart_value_aggregation"];
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "device_insights_charts_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_insights_charts_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_insights_charts_unit_id_fkey";
            columns: ["unit_id"];
            isOneToOne: false;
            referencedRelation: "physical_units";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_insights_charts_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_insights_charts_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      device_models: {
        Row: {
          created_at: string;
          id: string;
          manufacturer_id: string;
          name: string;
          slug: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: string;
          manufacturer_id: string;
          name: string;
          slug: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: string;
          manufacturer_id?: string;
          name?: string;
          slug?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "device_models_manufacturer_id_fkey";
            columns: ["manufacturer_id"];
            isOneToOne: false;
            referencedRelation: "manufacturers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_models_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_models_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      device_orders: {
        Row: {
          created_at: string;
          device_id: string;
          device_order_id: string | null;
          finished_at: string | null;
          name: string | null;
          order_id: string;
          parameters: Json | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["device_order_status"];
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          device_id: string;
          device_order_id?: string | null;
          finished_at?: string | null;
          name?: string | null;
          order_id: string;
          parameters?: Json | null;
          started_at?: string | null;
          status: Database["public"]["Enums"]["device_order_status"];
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          device_id?: string;
          device_order_id?: string | null;
          finished_at?: string | null;
          name?: string | null;
          order_id?: string;
          parameters?: Json | null;
          started_at?: string | null;
          status?: Database["public"]["Enums"]["device_order_status"];
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "device_orders_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
        ];
      };
      device_scalar_values: {
        Row: {
          base_unit_id: string | null;
          base_value: number | null;
          created_at: string;
          deployment_id: string;
          device_id: string;
          id: number;
          interval: number | null;
          name: string;
          unit_id: string;
          updated_at: string;
          value: number;
          workspace_id: string;
        };
        Insert: {
          base_unit_id?: string | null;
          base_value?: number | null;
          created_at?: string;
          deployment_id: string;
          device_id: string;
          id?: number;
          interval?: number | null;
          name: string;
          unit_id: string;
          updated_at?: string;
          value: number;
          workspace_id: string;
        };
        Update: {
          base_unit_id?: string | null;
          base_value?: number | null;
          created_at?: string;
          deployment_id?: string;
          device_id?: string;
          id?: number;
          interval?: number | null;
          name?: string;
          unit_id?: string;
          updated_at?: string;
          value?: number;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "device_scalar_values_base_unit_id_fkey";
            columns: ["base_unit_id"];
            isOneToOne: false;
            referencedRelation: "physical_units";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_scalar_values_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_scalar_values_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_scalar_values_unit_id_fkey";
            columns: ["unit_id"];
            isOneToOne: false;
            referencedRelation: "physical_units";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_scalar_values_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      device_tags: {
        Row: {
          created_at: string;
          description: string | null;
          id: string;
          meta_data: Json | null;
          name: string;
          parent_device_tag_id: string | null;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
        Insert: {
          created_at?: string;
          description?: string | null;
          id?: string;
          meta_data?: Json | null;
          name: string;
          parent_device_tag_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          created_at?: string;
          description?: string | null;
          id?: string;
          meta_data?: Json | null;
          name?: string;
          parent_device_tag_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "device_tags_parent_device_tag_id_fkey";
            columns: ["parent_device_tag_id"];
            isOneToOne: false;
            referencedRelation: "device_tags";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_tags_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "device_tags_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      devices: {
        Row: {
          created_at: string;
          deleted_at: string | null;
          deleted_by: string | null;
          deployment_id: string;
          id: string;
          last_auth: string | null;
          manufacturer_id: string | null;
          model_id: string;
          name: string;
          ref: string | null;
          software_version: string | null;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
        Insert: {
          created_at?: string;
          deleted_at?: string | null;
          deleted_by?: string | null;
          deployment_id: string;
          id?: string;
          last_auth?: string | null;
          manufacturer_id?: string | null;
          model_id: string;
          name: string;
          ref?: string | null;
          software_version?: string | null;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          created_at?: string;
          deleted_at?: string | null;
          deleted_by?: string | null;
          deployment_id?: string;
          id?: string;
          last_auth?: string | null;
          manufacturer_id?: string | null;
          model_id?: string;
          name?: string;
          ref?: string | null;
          software_version?: string | null;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "devices_deleted_by_fkey";
            columns: ["deleted_by"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_manufacturer_id_fkey";
            columns: ["manufacturer_id"];
            isOneToOne: false;
            referencedRelation: "manufacturers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_model_id_fkey";
            columns: ["model_id"];
            isOneToOne: false;
            referencedRelation: "device_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      devices_history: {
        Row: {
          created_at: string | null;
          deployment_id: string | null;
          device_id: string | null;
          id: string;
          new_val: Json | null;
          old_val: Json | null;
          operation: Database["public"]["Enums"]["db_operation"] | null;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string | null;
          deployment_id?: string | null;
          device_id?: string | null;
          id?: string;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string | null;
          deployment_id?: string | null;
          device_id?: string | null;
          id?: string;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "devices_history_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_history_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_history_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      devices_to_device_tags: {
        Row: {
          created_at: string;
          device_id: string;
          device_tag_id: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          device_id: string;
          device_tag_id: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          device_id?: string;
          device_tag_id?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "devices_to_device_tags_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_to_device_tags_device_tag_id_fkey";
            columns: ["device_tag_id"];
            isOneToOne: false;
            referencedRelation: "device_tags";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "devices_to_device_tags_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      documents: {
        Row: {
          created_at: string;
          device_model_id: string | null;
          id: string;
          manufacturer_id: string | null;
          title: string | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          device_model_id?: string | null;
          id: string;
          manufacturer_id?: string | null;
          title?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          device_model_id?: string | null;
          id?: string;
          manufacturer_id?: string | null;
          title?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "documents_device_model_id_fkey";
            columns: ["device_model_id"];
            isOneToOne: false;
            referencedRelation: "device_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "documents_manufacturer_id_fkey";
            columns: ["manufacturer_id"];
            isOneToOne: false;
            referencedRelation: "manufacturers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "documents_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      gopg_migrations: {
        Row: {
          created_at: string | null;
          id: number;
          version: number | null;
        };
        Insert: {
          created_at?: string | null;
          id?: number;
          version?: number | null;
        };
        Update: {
          created_at?: string | null;
          id?: number;
          version?: number | null;
        };
        Relationships: [];
      };
      maintenance_approval_files: {
        Row: {
          bucket_id: string;
          created_at: string;
          description: string;
          id: string;
          maintenance_approval_id: string;
          path: string;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          bucket_id: string;
          created_at?: string;
          description?: string;
          id?: string;
          maintenance_approval_id: string;
          path: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          bucket_id?: string;
          created_at?: string;
          description?: string;
          id?: string;
          maintenance_approval_id?: string;
          path?: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_approval_files_maintenance_approval_id_fkey";
            columns: ["maintenance_approval_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_approvals";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_approval_files_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_approvals: {
        Row: {
          approver_email: string | null;
          approver_id: string | null;
          approver_name: string | null;
          created_at: string;
          id: string;
          maintenance_id: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          approver_email?: string | null;
          approver_id?: string | null;
          approver_name?: string | null;
          created_at?: string;
          id?: string;
          maintenance_id: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          approver_email?: string | null;
          approver_id?: string | null;
          approver_name?: string | null;
          created_at?: string;
          id?: string;
          maintenance_id?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_approvals_approver_id_fkey";
            columns: ["approver_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_approvals_maintenance_id_fkey";
            columns: ["maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_approvals_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_approvals_history: {
        Row: {
          created_at: string | null;
          id: string;
          maintenance_approval_id: string | null;
          maintenance_id: string | null;
          new_val: Json | null;
          old_val: Json | null;
          operation: Database["public"]["Enums"]["db_operation"] | null;
          user_id: string | null;
        };
        Insert: {
          created_at?: string | null;
          id?: string;
          maintenance_approval_id?: string | null;
          maintenance_id?: string | null;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
        };
        Update: {
          created_at?: string | null;
          id?: string;
          maintenance_approval_id?: string | null;
          maintenance_id?: string | null;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_approvals_history_maintenance_approval_id_fkey";
            columns: ["maintenance_approval_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_approvals";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_approvals_history_maintenance_id_fkey";
            columns: ["maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_approvals_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_item_files: {
        Row: {
          bucket_id: string;
          created_at: string;
          id: string;
          maintenance_item_id: string;
          path: string;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          bucket_id: string;
          created_at?: string;
          id?: string;
          maintenance_item_id: string;
          path: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          bucket_id?: string;
          created_at?: string;
          id?: string;
          maintenance_item_id?: string;
          path?: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_item_files_maintenance_item_id_fkey";
            columns: ["maintenance_item_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_item_files_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_item_template_files: {
        Row: {
          bucket_id: string;
          checklist_template_item_file_id: string | null;
          created_at: string;
          description: string;
          id: string;
          maintenance_item_id: string;
          ordering: number;
          path: string;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          bucket_id: string;
          checklist_template_item_file_id?: string | null;
          created_at?: string;
          description?: string;
          id?: string;
          maintenance_item_id: string;
          ordering?: number;
          path: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          bucket_id?: string;
          checklist_template_item_file_id?: string | null;
          created_at?: string;
          description?: string;
          id?: string;
          maintenance_item_id?: string;
          ordering?: number;
          path?: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_item_template_fil_checklist_template_item_file_fkey";
            columns: ["checklist_template_item_file_id"];
            isOneToOne: false;
            referencedRelation: "checklist_template_item_files";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_item_template_files_maintenance_item_id_fkey";
            columns: ["maintenance_item_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_item_template_files_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_items: {
        Row: {
          assignee_id: string | null;
          checklist_template_item_id: string | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          id: string;
          is_custom: boolean | null;
          maintenance_id: string | null;
          name: string;
          notes: string;
          ordering: number;
          resolved_at: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_item_status"];
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
        Insert: {
          assignee_id?: string | null;
          checklist_template_item_id?: string | null;
          created_at?: string;
          deployment_id: string;
          description?: string | null;
          device_id?: string | null;
          id?: string;
          is_custom?: boolean | null;
          maintenance_id?: string | null;
          name: string;
          notes?: string;
          ordering?: number;
          resolved_at?: string | null;
          started_at?: string | null;
          status?: Database["public"]["Enums"]["maintenance_item_status"];
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          assignee_id?: string | null;
          checklist_template_item_id?: string | null;
          created_at?: string;
          deployment_id?: string;
          description?: string | null;
          device_id?: string | null;
          id?: string;
          is_custom?: boolean | null;
          maintenance_id?: string | null;
          name?: string;
          notes?: string;
          ordering?: number;
          resolved_at?: string | null;
          started_at?: string | null;
          status?: Database["public"]["Enums"]["maintenance_item_status"];
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_items_assignee_id_fkey";
            columns: ["assignee_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_checklist_template_item_id_fkey";
            columns: ["checklist_template_item_id"];
            isOneToOne: false;
            referencedRelation: "checklist_template_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_maintenance_id_fkey";
            columns: ["maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_items_to_parts: {
        Row: {
          action: Database["public"]["Enums"]["part_usage_type"];
          created_at: string;
          id: string;
          maintenance_item_id: string;
          notes: string | null;
          part_category_id: string | null;
          part_id: string | null;
          part_model_id: string | null;
          quantity: number | null;
          skip_inventory: boolean | null;
          type: Database["public"]["Enums"]["maintenance_items_to_parts_type"] | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          action?: Database["public"]["Enums"]["part_usage_type"];
          created_at?: string;
          id?: string;
          maintenance_item_id: string;
          notes?: string | null;
          part_category_id?: string | null;
          part_id?: string | null;
          part_model_id?: string | null;
          quantity?: number | null;
          skip_inventory?: boolean | null;
          type?: Database["public"]["Enums"]["maintenance_items_to_parts_type"] | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          action?: Database["public"]["Enums"]["part_usage_type"];
          created_at?: string;
          id?: string;
          maintenance_item_id?: string;
          notes?: string | null;
          part_category_id?: string | null;
          part_id?: string | null;
          part_model_id?: string | null;
          quantity?: number | null;
          skip_inventory?: boolean | null;
          type?: Database["public"]["Enums"]["maintenance_items_to_parts_type"] | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_items_to_parts_maintenance_item_id_fkey";
            columns: ["maintenance_item_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_to_parts_part_category_id_fkey";
            columns: ["part_category_id"];
            isOneToOne: false;
            referencedRelation: "part_categories";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_to_parts_part_id_fkey";
            columns: ["part_id"];
            isOneToOne: false;
            referencedRelation: "parts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_to_parts_part_model_id_fkey";
            columns: ["part_model_id"];
            isOneToOne: false;
            referencedRelation: "part_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_to_parts_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_items_to_parts_to_part_locations: {
        Row: {
          created_at: string;
          maintenance_item_id: string;
          part_id: string;
          part_location_id: string;
          quantity: number;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          maintenance_item_id: string;
          part_id: string;
          part_location_id: string;
          quantity: number;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          maintenance_item_id?: string;
          part_id?: string;
          part_location_id?: string;
          quantity?: number;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_items_to_parts_to_part_loc_maintenance_item_id_fkey";
            columns: ["maintenance_item_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_to_parts_to_part_locati_part_location_id_fkey";
            columns: ["part_location_id"];
            isOneToOne: false;
            referencedRelation: "part_locations";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_to_parts_to_part_locations_part_id_fkey";
            columns: ["part_id"];
            isOneToOne: false;
            referencedRelation: "parts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_items_to_parts_to_part_locations_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_strategies: {
        Row: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          description: string;
          id: string;
          is_active: boolean;
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
        Insert: {
          assignee_id?: string | null;
          created_at?: string;
          deployment_id: string;
          description?: string;
          id?: string;
          is_active?: boolean;
          title: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          assignee_id?: string | null;
          created_at?: string;
          deployment_id?: string;
          description?: string;
          id?: string;
          is_active?: boolean;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_strategies_assignee_id_fkey";
            columns: ["assignee_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_strategies_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_strategies_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_strategies_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_strategies_to_devices: {
        Row: {
          checklist_template_id: string | null;
          created_at: string;
          device_id: string;
          maintenance_strategy_id: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          checklist_template_id?: string | null;
          created_at?: string;
          device_id: string;
          maintenance_strategy_id: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          checklist_template_id?: string | null;
          created_at?: string;
          device_id?: string;
          maintenance_strategy_id?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_strategies_to_devices_checklist_template_id_fkey";
            columns: ["checklist_template_id"];
            isOneToOne: false;
            referencedRelation: "checklist_templates";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_strategies_to_devices_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_strategies_to_devices_maintenance_strategy_id_fkey";
            columns: ["maintenance_strategy_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_strategies";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_strategies_to_devices_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_strategy_intervals: {
        Row: {
          created_at: string;
          id: string;
          maintenance_strategy_id: string;
          schedule_interval: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: string;
          maintenance_strategy_id: string;
          schedule_interval: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: string;
          maintenance_strategy_id?: string;
          schedule_interval?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_strategy_intervals_maintenance_strategy_id_fkey";
            columns: ["maintenance_strategy_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_strategies";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_strategy_intervals_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_strategy_triggers: {
        Row: {
          created_at: string;
          evaluate_every: number | null;
          id: string;
          last_evaluated_at: string;
          maintenance_strategy_id: string;
          metric: Database["public"]["Enums"]["trigger_metric_type"];
          start_at: string;
          threshold: number;
          updated_at: string;
          user_id: string | null;
          window_size: number | null;
        };
        Insert: {
          created_at?: string;
          evaluate_every?: number | null;
          id?: string;
          last_evaluated_at?: string;
          maintenance_strategy_id: string;
          metric: Database["public"]["Enums"]["trigger_metric_type"];
          start_at?: string;
          threshold: number;
          updated_at?: string;
          user_id?: string | null;
          window_size?: number | null;
        };
        Update: {
          created_at?: string;
          evaluate_every?: number | null;
          id?: string;
          last_evaluated_at?: string;
          maintenance_strategy_id?: string;
          metric?: Database["public"]["Enums"]["trigger_metric_type"];
          start_at?: string;
          threshold?: number;
          updated_at?: string;
          user_id?: string | null;
          window_size?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_strategy_triggers_maintenance_strategy_id_fkey";
            columns: ["maintenance_strategy_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_strategies";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_strategy_triggers_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_tags: {
        Row: {
          created_at: string;
          description: string | null;
          id: string;
          name: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string;
          description?: string | null;
          id?: string;
          name: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string;
          description?: string | null;
          id?: string;
          name?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_tags_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_tags_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenance_trigger_to_device_metrics: {
        Row: {
          created_at: string;
          device_id: string;
          last_maintained_at: string | null;
          metric_value: number | null;
          trigger_id: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          device_id: string;
          last_maintained_at?: string | null;
          metric_value?: number | null;
          trigger_id: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          device_id?: string;
          last_maintained_at?: string | null;
          metric_value?: number | null;
          trigger_id?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenance_trigger_to_device_metrics_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_trigger_to_device_metrics_trigger_id_fkey";
            columns: ["trigger_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_strategy_triggers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenance_trigger_to_device_metrics_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenances: {
        Row: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
          parent_maintenance: Database["public"]["Tables"]["maintenances"]["Row"][];
        };
        Insert: {
          assignee_id?: string | null;
          created_at?: string;
          deployment_id: string;
          id?: string;
          maintenance_strategy_id?: string | null;
          notes?: string;
          parent_maintenance_id?: string | null;
          planned_at?: string | null;
          planned_date?: string | null;
          planned_until?: string | null;
          public_id?: string;
          resolution_time?: number | null;
          resolved_at?: string | null;
          scheduled_at?: string | null;
          serial_id?: number;
          serial_id_prefix?: string;
          skip_reason?: string | null;
          started_at?: string | null;
          status?: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          assignee_id?: string | null;
          created_at?: string;
          deployment_id?: string;
          id?: string;
          maintenance_strategy_id?: string | null;
          notes?: string;
          parent_maintenance_id?: string | null;
          planned_at?: string | null;
          planned_date?: string | null;
          planned_until?: string | null;
          public_id?: string;
          resolution_time?: number | null;
          resolved_at?: string | null;
          scheduled_at?: string | null;
          serial_id?: number;
          serial_id_prefix?: string;
          skip_reason?: string | null;
          started_at?: string | null;
          status?: Database["public"]["Enums"]["maintenance_status"];
          title?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "maintenances_assignee_id_fkey";
            columns: ["assignee_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_maintenance_strategy_id_fkey";
            columns: ["maintenance_strategy_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_strategies";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_parent_maintenance_id_fkey";
            columns: ["parent_maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenances_history: {
        Row: {
          created_at: string | null;
          deployment_id: string | null;
          id: string;
          maintenance_id: string | null;
          new_val: Json | null;
          old_val: Json | null;
          operation: Database["public"]["Enums"]["db_operation"] | null;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string | null;
          deployment_id?: string | null;
          id?: string;
          maintenance_id?: string | null;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string | null;
          deployment_id?: string | null;
          id?: string;
          maintenance_id?: string | null;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenances_history_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_history_maintenance_id_fkey";
            columns: ["maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_history_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenances_to_devices: {
        Row: {
          checklist_template_id: string | null;
          created_at: string;
          device_id: string;
          maintenance_id: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          checklist_template_id?: string | null;
          created_at?: string;
          device_id: string;
          maintenance_id: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          checklist_template_id?: string | null;
          created_at?: string;
          device_id?: string;
          maintenance_id?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenances_to_devices_checklist_template_id_fkey";
            columns: ["checklist_template_id"];
            isOneToOne: false;
            referencedRelation: "checklist_templates";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_devices_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_devices_maintenance_id_fkey";
            columns: ["maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_devices_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenances_to_devices_history: {
        Row: {
          created_at: string | null;
          device_id: string | null;
          id: string;
          maintenance_id: string | null;
          new_val: Json | null;
          old_val: Json | null;
          operation: Database["public"]["Enums"]["db_operation"] | null;
          user_id: string | null;
        };
        Insert: {
          created_at?: string | null;
          device_id?: string | null;
          id?: string;
          maintenance_id?: string | null;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
        };
        Update: {
          created_at?: string | null;
          device_id?: string | null;
          id?: string;
          maintenance_id?: string | null;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenances_to_devices_history_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_devices_history_maintenance_id_fkey";
            columns: ["maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_devices_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenances_to_maintenance_tags: {
        Row: {
          created_at: string;
          maintenance_id: string;
          maintenance_tag_id: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          maintenance_id: string;
          maintenance_tag_id: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          maintenance_id?: string;
          maintenance_tag_id?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenances_to_maintenance_tags_maintenance_id_fkey";
            columns: ["maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_maintenance_tags_maintenance_tag_id_fkey";
            columns: ["maintenance_tag_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_tags";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_maintenance_tags_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      maintenances_to_maintenance_tags_history: {
        Row: {
          created_at: string | null;
          id: string;
          maintenance_id: string | null;
          maintenance_tag_id: string | null;
          new_val: Json | null;
          old_val: Json | null;
          operation: Database["public"]["Enums"]["db_operation"] | null;
          user_id: string | null;
        };
        Insert: {
          created_at?: string | null;
          id?: string;
          maintenance_id?: string | null;
          maintenance_tag_id?: string | null;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
        };
        Update: {
          created_at?: string | null;
          id?: string;
          maintenance_id?: string | null;
          maintenance_tag_id?: string | null;
          new_val?: Json | null;
          old_val?: Json | null;
          operation?: Database["public"]["Enums"]["db_operation"] | null;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "maintenances_to_maintenance_tags_histor_maintenance_tag_id_fkey";
            columns: ["maintenance_tag_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_tags";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_maintenance_tags_history_maintenance_id_fkey";
            columns: ["maintenance_id"];
            isOneToOne: false;
            referencedRelation: "maintenances";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "maintenances_to_maintenance_tags_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      manufacturers: {
        Row: {
          created_at: string;
          id: string;
          name: string;
          slug: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: string;
          name: string;
          slug: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: string;
          name?: string;
          slug?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "manufacturers_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "manufacturers_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      part_categories: {
        Row: {
          created_at: string;
          id: string;
          name: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: string;
          name: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: string;
          name?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_categories_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_categories_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      part_inventories: {
        Row: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          action?: Database["public"]["Enums"]["inventory_action"];
          created_at?: string;
          deployment_id: string;
          executed_at?: string;
          id?: string;
          maintenance_item_id?: string | null;
          note?: string | null;
          package_number?: string | null;
          package_price?: number | null;
          part_id: string;
          quantity?: number | null;
          source_part_id?: string | null;
          target_part_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          action?: Database["public"]["Enums"]["inventory_action"];
          created_at?: string;
          deployment_id?: string;
          executed_at?: string;
          id?: string;
          maintenance_item_id?: string | null;
          note?: string | null;
          package_number?: string | null;
          package_price?: number | null;
          part_id?: string;
          quantity?: number | null;
          source_part_id?: string | null;
          target_part_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_inventories_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_inventories_maintenance_item_id_fkey";
            columns: ["maintenance_item_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_inventories_part_id_fkey";
            columns: ["part_id"];
            isOneToOne: false;
            referencedRelation: "parts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_inventories_source_part_id_fkey";
            columns: ["source_part_id"];
            isOneToOne: false;
            referencedRelation: "parts_history";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_inventories_target_part_id_fkey";
            columns: ["target_part_id"];
            isOneToOne: false;
            referencedRelation: "parts_history";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_inventories_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      part_inventories_to_locations: {
        Row: {
          created_at: string;
          id: string;
          location_type: Database["public"]["Enums"]["inventory_location_type"];
          part_inventory_id: string;
          part_location_id: string;
          quantity: number;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: string;
          location_type: Database["public"]["Enums"]["inventory_location_type"];
          part_inventory_id: string;
          part_location_id: string;
          quantity: number;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: string;
          location_type?: Database["public"]["Enums"]["inventory_location_type"];
          part_inventory_id?: string;
          part_location_id?: string;
          quantity?: number;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_inventories_to_locations_part_inventory_id_fkey";
            columns: ["part_inventory_id"];
            isOneToOne: false;
            referencedRelation: "part_inventories";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_inventories_to_locations_part_location_id_fkey";
            columns: ["part_location_id"];
            isOneToOne: false;
            referencedRelation: "part_locations";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_inventories_to_locations_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      part_inventory_files: {
        Row: {
          bucket_id: string;
          created_at: string;
          description: string;
          id: string;
          part_inventory_id: string;
          path: string;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          bucket_id: string;
          created_at?: string;
          description?: string;
          id?: string;
          part_inventory_id: string;
          path: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          bucket_id?: string;
          created_at?: string;
          description?: string;
          id?: string;
          part_inventory_id?: string;
          path?: string;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_inventory_files_part_inventory_id_fkey";
            columns: ["part_inventory_id"];
            isOneToOne: false;
            referencedRelation: "part_inventories";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_inventory_files_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      part_location_templates: {
        Row: {
          created_at: string;
          deployment_id: string;
          id: string;
          name: string;
          parent_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          deployment_id: string;
          id?: string;
          name: string;
          parent_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          deployment_id?: string;
          id?: string;
          name?: string;
          parent_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_location_templates_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_location_templates_parent_id_fkey";
            columns: ["parent_id"];
            isOneToOne: false;
            referencedRelation: "part_location_templates";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_location_templates_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      part_locations: {
        Row: {
          created_at: string;
          deployment_id: string;
          id: string;
          level: number;
          name: string;
          parent_part_location_id: string | null;
          part_location_template_id: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          deployment_id: string;
          id?: string;
          level?: number;
          name: string;
          parent_part_location_id?: string | null;
          part_location_template_id: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          deployment_id?: string;
          id?: string;
          level?: number;
          name?: string;
          parent_part_location_id?: string | null;
          part_location_template_id?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_locations_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_locations_parent_part_location_id_fkey";
            columns: ["parent_part_location_id"];
            isOneToOne: false;
            referencedRelation: "part_locations";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_locations_part_location_template_id_fkey";
            columns: ["part_location_template_id"];
            isOneToOne: false;
            referencedRelation: "part_location_templates";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_locations_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      part_manufacturers: {
        Row: {
          created_at: string;
          id: string;
          name: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: string;
          name: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: string;
          name?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_manufacturers_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_manufacturers_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      part_model_constraints: {
        Row: {
          created_at: string;
          current_quantity: number | null;
          deployment_id: string;
          description: string | null;
          id: string;
          max_quantity: number | null;
          min_quantity: number | null;
          part_model_id: string;
          quantity_above_max: boolean | null;
          quantity_below_min: boolean | null;
          quantity_equals_max: boolean | null;
          quantity_equals_min: boolean | null;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          current_quantity?: number | null;
          deployment_id: string;
          description?: string | null;
          id?: string;
          max_quantity?: number | null;
          min_quantity?: number | null;
          part_model_id: string;
          quantity_above_max?: boolean | null;
          quantity_below_min?: boolean | null;
          quantity_equals_max?: boolean | null;
          quantity_equals_min?: boolean | null;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          current_quantity?: number | null;
          deployment_id?: string;
          description?: string | null;
          id?: string;
          max_quantity?: number | null;
          min_quantity?: number | null;
          part_model_id?: string;
          quantity_above_max?: boolean | null;
          quantity_below_min?: boolean | null;
          quantity_equals_max?: boolean | null;
          quantity_equals_min?: boolean | null;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_model_constraints_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_model_constraints_part_model_id_fkey";
            columns: ["part_model_id"];
            isOneToOne: false;
            referencedRelation: "part_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_model_constraints_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      part_models: {
        Row: {
          created_at: string;
          description: string | null;
          id: string;
          manufacturer_ref: string | null;
          name: string;
          order_information: string | null;
          order_lot_size: number | null;
          order_min_quantity: number | null;
          part_category_id: string;
          part_manufacturer_id: string;
          ref: string | null;
          supplier: string | null;
          supplier_part_ref: string | null;
          supplier_ref: string | null;
          updated_at: string;
          user_id: string | null;
          workspace_id: string | null;
        };
        Insert: {
          created_at?: string;
          description?: string | null;
          id?: string;
          manufacturer_ref?: string | null;
          name: string;
          order_information?: string | null;
          order_lot_size?: number | null;
          order_min_quantity?: number | null;
          part_category_id: string;
          part_manufacturer_id: string;
          ref?: string | null;
          supplier?: string | null;
          supplier_part_ref?: string | null;
          supplier_ref?: string | null;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Update: {
          created_at?: string;
          description?: string | null;
          id?: string;
          manufacturer_ref?: string | null;
          name?: string;
          order_information?: string | null;
          order_lot_size?: number | null;
          order_min_quantity?: number | null;
          part_category_id?: string;
          part_manufacturer_id?: string;
          ref?: string | null;
          supplier?: string | null;
          supplier_part_ref?: string | null;
          supplier_ref?: string | null;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "part_models_part_category_id_fkey";
            columns: ["part_category_id"];
            isOneToOne: false;
            referencedRelation: "part_categories";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_models_part_manufacturer_id_fkey";
            columns: ["part_manufacturer_id"];
            isOneToOne: false;
            referencedRelation: "part_manufacturers";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_models_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_models_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      part_to_part_locations: {
        Row: {
          created_at: string;
          part_id: string;
          part_location_id: string;
          quantity: number;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          part_id: string;
          part_location_id: string;
          quantity: number;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          part_id?: string;
          part_location_id?: string;
          quantity?: number;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "part_to_part_locations_part_id_fkey";
            columns: ["part_id"];
            isOneToOne: false;
            referencedRelation: "parts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "part_to_part_locations_part_location_id_fkey";
            columns: ["part_location_id"];
            isOneToOne: false;
            referencedRelation: "part_locations";
            referencedColumns: ["id"];
          },
        ];
      };
      parts: {
        Row: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          archived_at?: string | null;
          average_price?: number | null;
          created_at?: string;
          deployment_id: string;
          description?: string | null;
          device_id?: string | null;
          executed_at?: string;
          id?: string;
          part_model_id: string;
          quantity?: number | null;
          ref?: string | null;
          transient_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          archived_at?: string | null;
          average_price?: number | null;
          created_at?: string;
          deployment_id?: string;
          description?: string | null;
          device_id?: string | null;
          executed_at?: string;
          id?: string;
          part_model_id?: string;
          quantity?: number | null;
          ref?: string | null;
          transient_id?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "parts_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "parts_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "parts_part_model_id_fkey";
            columns: ["part_model_id"];
            isOneToOne: false;
            referencedRelation: "part_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "parts_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      parts_history: {
        Row: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          part_id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          seq_id: number;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          archived_at?: string | null;
          average_price?: number | null;
          created_at?: string;
          deployment_id: string;
          description?: string | null;
          device_id?: string | null;
          executed_at?: string;
          id?: string;
          maintenance_item_id?: string | null;
          part_id: string;
          part_model_id: string;
          quantity?: number | null;
          ref?: string | null;
          seq_id?: number;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          archived_at?: string | null;
          average_price?: number | null;
          created_at?: string;
          deployment_id?: string;
          description?: string | null;
          device_id?: string | null;
          executed_at?: string;
          id?: string;
          maintenance_item_id?: string | null;
          part_id?: string;
          part_model_id?: string;
          quantity?: number | null;
          ref?: string | null;
          seq_id?: number;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "parts_history_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "parts_history_device_id_fkey";
            columns: ["device_id"];
            isOneToOne: false;
            referencedRelation: "devices";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "parts_history_maintenance_item_id_fkey";
            columns: ["maintenance_item_id"];
            isOneToOne: false;
            referencedRelation: "maintenance_items";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "parts_history_part_id_fkey";
            columns: ["part_id"];
            isOneToOne: false;
            referencedRelation: "parts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "parts_history_part_model_id_fkey";
            columns: ["part_model_id"];
            isOneToOne: false;
            referencedRelation: "part_models";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "parts_history_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      physical_units: {
        Row: {
          base_unit_id: string | null;
          conversion_factor: number;
          conversion_offset: number;
          description: string | null;
          id: string;
          name: string;
          symbol: string;
          type: string;
        };
        Insert: {
          base_unit_id?: string | null;
          conversion_factor: number;
          conversion_offset: number;
          description?: string | null;
          id: string;
          name: string;
          symbol: string;
          type: string;
        };
        Update: {
          base_unit_id?: string | null;
          conversion_factor?: number;
          conversion_offset?: number;
          description?: string | null;
          id?: string;
          name?: string;
          symbol?: string;
          type?: string;
        };
        Relationships: [
          {
            foreignKeyName: "physical_units_base_unit_id_fkey";
            columns: ["base_unit_id"];
            isOneToOne: false;
            referencedRelation: "physical_units";
            referencedColumns: ["id"];
          },
        ];
      };
      user_profiles: {
        Row: {
          api_key: string | null;
          created_at: string;
          email: string;
          first_name: string;
          id: string;
          last_name: string;
          locale: string;
          phone: string | null;
          updated_at: string;
        };
        Insert: {
          api_key?: string | null;
          created_at?: string;
          email: string;
          first_name?: string;
          id: string;
          last_name?: string;
          locale?: string;
          phone?: string | null;
          updated_at?: string;
        };
        Update: {
          api_key?: string | null;
          created_at?: string;
          email?: string;
          first_name?: string;
          id?: string;
          last_name?: string;
          locale?: string;
          phone?: string | null;
          updated_at?: string;
        };
        Relationships: [];
      };
      users_to_deployments: {
        Row: {
          created_at: string;
          deployment_id: string;
          role: Database["public"]["Enums"]["deployment_role"];
          updated_at: string;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          deployment_id: string;
          role?: Database["public"]["Enums"]["deployment_role"];
          updated_at?: string;
          user_id: string;
        };
        Update: {
          created_at?: string;
          deployment_id?: string;
          role?: Database["public"]["Enums"]["deployment_role"];
          updated_at?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "users_to_deployments_deployment_id_fkey";
            columns: ["deployment_id"];
            isOneToOne: false;
            referencedRelation: "deployments";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "users_to_deployments_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      users_to_roles: {
        Row: {
          created_at: string;
          role: Database["public"]["Enums"]["global_role"];
          updated_at: string;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          role?: Database["public"]["Enums"]["global_role"];
          updated_at?: string;
          user_id: string;
        };
        Update: {
          created_at?: string;
          role?: Database["public"]["Enums"]["global_role"];
          updated_at?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "users_to_roles_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: true;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
        ];
      };
      users_to_workspaces: {
        Row: {
          created_at: string;
          role: Database["public"]["Enums"]["workspace_role"];
          updated_at: string;
          user_id: string;
          workspace_id: string;
        };
        Insert: {
          created_at?: string;
          role?: Database["public"]["Enums"]["workspace_role"];
          updated_at?: string;
          user_id: string;
          workspace_id: string;
        };
        Update: {
          created_at?: string;
          role?: Database["public"]["Enums"]["workspace_role"];
          updated_at?: string;
          user_id?: string;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "users_to_workspaces_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "users_to_workspaces_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      whitelabel_brands: {
        Row: {
          app_url: string;
          created_at: string;
          email_logo: string;
          id: string;
          key: string;
          logo: string;
          name: string;
          primary_color: string;
          secondary_color: string;
          updated_at: string;
        };
        Insert: {
          app_url: string;
          created_at?: string;
          email_logo: string;
          id?: string;
          key: string;
          logo: string;
          name: string;
          primary_color: string;
          secondary_color: string;
          updated_at?: string;
        };
        Update: {
          app_url?: string;
          created_at?: string;
          email_logo?: string;
          id?: string;
          key?: string;
          logo?: string;
          name?: string;
          primary_color?: string;
          secondary_color?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      workspace_invitations: {
        Row: {
          created_at: string;
          email: string;
          id: string;
          updated_at: string;
          user_id: string;
          user_name: string;
          workspace_id: string;
          workspace_name: string;
          workspace_role: Database["public"]["Enums"]["workspace_role"];
        };
        Insert: {
          created_at?: string;
          email: string;
          id?: string;
          updated_at?: string;
          user_id: string;
          user_name: string;
          workspace_id: string;
          workspace_name: string;
          workspace_role?: Database["public"]["Enums"]["workspace_role"];
        };
        Update: {
          created_at?: string;
          email?: string;
          id?: string;
          updated_at?: string;
          user_id?: string;
          user_name?: string;
          workspace_id?: string;
          workspace_name?: string;
          workspace_role?: Database["public"]["Enums"]["workspace_role"];
        };
        Relationships: [
          {
            foreignKeyName: "workspace_invitations_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "workspace_invitations_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      workspace_profile_files: {
        Row: {
          bucket_id: string;
          created_at: string;
          id: string;
          path: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
        Insert: {
          bucket_id: string;
          created_at?: string;
          id?: string;
          path: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id: string;
        };
        Update: {
          bucket_id?: string;
          created_at?: string;
          id?: string;
          path?: string;
          updated_at?: string;
          user_id?: string | null;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "workspace_profile_files_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "workspace_profile_files_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspace_profiles";
            referencedColumns: ["workspace_id"];
          },
        ];
      };
      workspace_profiles: {
        Row: {
          created_at: string;
          description: string;
          is_public: boolean | null;
          name: string;
          updated_at: string;
          website: string;
          workspace_id: string;
        };
        Insert: {
          created_at?: string;
          description?: string;
          is_public?: boolean | null;
          name?: string;
          updated_at?: string;
          website?: string;
          workspace_id: string;
        };
        Update: {
          created_at?: string;
          description?: string;
          is_public?: boolean | null;
          name?: string;
          updated_at?: string;
          website?: string;
          workspace_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "workspace_profiles_workspace_id_fkey";
            columns: ["workspace_id"];
            isOneToOne: true;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
        ];
      };
      workspaces: {
        Row: {
          abbreviation: string;
          address_1: string | null;
          address_2: string | null;
          business_type: Database["public"]["Enums"]["business_type"] | null;
          city: string | null;
          country: string | null;
          created_at: string;
          creator_workspace_id: string | null;
          deleted_at: string | null;
          id: string;
          is_paid: boolean;
          name: string;
          share_code: string;
          trial_end_date: string | null;
          updated_at: string;
          user_id: string | null;
          whitelabel_brand_id: string | null;
          zipcode: string | null;
        };
        Insert: {
          abbreviation?: string;
          address_1?: string | null;
          address_2?: string | null;
          business_type?: Database["public"]["Enums"]["business_type"] | null;
          city?: string | null;
          country?: string | null;
          created_at?: string;
          creator_workspace_id?: string | null;
          deleted_at?: string | null;
          id?: string;
          is_paid?: boolean;
          name: string;
          share_code?: string;
          trial_end_date?: string | null;
          updated_at?: string;
          user_id?: string | null;
          whitelabel_brand_id?: string | null;
          zipcode?: string | null;
        };
        Update: {
          abbreviation?: string;
          address_1?: string | null;
          address_2?: string | null;
          business_type?: Database["public"]["Enums"]["business_type"] | null;
          city?: string | null;
          country?: string | null;
          created_at?: string;
          creator_workspace_id?: string | null;
          deleted_at?: string | null;
          id?: string;
          is_paid?: boolean;
          name?: string;
          share_code?: string;
          trial_end_date?: string | null;
          updated_at?: string;
          user_id?: string | null;
          whitelabel_brand_id?: string | null;
          zipcode?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "workspaces_creator_workspace_id_fkey";
            columns: ["creator_workspace_id"];
            isOneToOne: false;
            referencedRelation: "workspaces";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "workspaces_user_id_fkey";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "user_profiles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "workspaces_whitelabel_brand_id_fkey";
            columns: ["whitelabel_brand_id"];
            isOneToOne: false;
            referencedRelation: "whitelabel_brands";
            referencedColumns: ["id"];
          },
        ];
      };
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      accept_deployment_invitation: {
        Args: { invitation_id: string };
        Returns: undefined;
      };
      accept_workspace_invitation: {
        Args: { invitation_id: string };
        Returns: undefined;
      };
      add_compression_policy: {
        Args: {
          hypertable: unknown;
          compress_after?: unknown;
          if_not_exists?: boolean;
          schedule_interval?: unknown;
          initial_start?: string;
          timezone?: string;
          compress_created_before?: unknown;
        };
        Returns: number;
      };
      add_continuous_aggregate_policy: {
        Args: {
          continuous_aggregate: unknown;
          start_offset: unknown;
          end_offset: unknown;
          schedule_interval: unknown;
          if_not_exists?: boolean;
          initial_start?: string;
          timezone?: string;
        };
        Returns: number;
      };
      add_dimension: {
        Args:
          | {
              hypertable: unknown;
              column_name: unknown;
              number_partitions?: number;
              chunk_time_interval?: unknown;
              partitioning_func?: unknown;
              if_not_exists?: boolean;
            }
          | { hypertable: unknown; dimension: unknown; if_not_exists?: boolean };
        Returns: {
          dimension_id: number;
          schema_name: unknown;
          table_name: unknown;
          column_name: unknown;
          created: boolean;
        }[];
      };
      add_job: {
        Args: {
          proc: unknown;
          schedule_interval: unknown;
          config?: Json;
          initial_start?: string;
          scheduled?: boolean;
          check_config?: unknown;
          fixed_schedule?: boolean;
          timezone?: string;
        };
        Returns: number;
      };
      add_part_to_maintenance_item: {
        Args: {
          _maintenance_item_id: string;
          _part_id: string;
          _quantity: number;
          _action: Database["public"]["Enums"]["part_usage_type"];
          _auto_select_locations?: boolean;
        };
        Returns: {
          action: Database["public"]["Enums"]["part_usage_type"];
          created_at: string;
          id: string;
          maintenance_item_id: string;
          notes: string | null;
          part_category_id: string | null;
          part_id: string | null;
          part_model_id: string | null;
          quantity: number | null;
          skip_inventory: boolean | null;
          type: Database["public"]["Enums"]["maintenance_items_to_parts_type"] | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      add_reorder_policy: {
        Args: {
          hypertable: unknown;
          index_name: unknown;
          if_not_exists?: boolean;
          initial_start?: string;
          timezone?: string;
        };
        Returns: number;
      };
      add_retention_policy: {
        Args: {
          relation: unknown;
          drop_after?: unknown;
          if_not_exists?: boolean;
          schedule_interval?: unknown;
          initial_start?: string;
          timezone?: string;
          drop_created_before?: unknown;
        };
        Returns: number;
      };
      alter_job: {
        Args: {
          job_id: number;
          schedule_interval?: unknown;
          max_runtime?: unknown;
          max_retries?: number;
          retry_period?: unknown;
          scheduled?: boolean;
          config?: Json;
          next_start?: string;
          if_exists?: boolean;
          check_config?: unknown;
          fixed_schedule?: boolean;
          initial_start?: string;
          timezone?: string;
        };
        Returns: {
          job_id: number;
          schedule_interval: unknown;
          max_runtime: unknown;
          max_retries: number;
          retry_period: unknown;
          scheduled: boolean;
          config: Json;
          next_start: string;
          check_config: string;
          fixed_schedule: boolean;
          initial_start: string;
          timezone: string;
        }[];
      };
      apply_spare_part_usage: {
        Args: { _maintenance_item_id: string };
        Returns: undefined;
      };
      approximate_row_count: {
        Args: { relation: unknown };
        Returns: number;
      };
      attach_tablespace: {
        Args: {
          tablespace: unknown;
          hypertable: unknown;
          if_not_attached?: boolean;
        };
        Returns: undefined;
      };
      by_hash: {
        Args: {
          column_name: unknown;
          number_partitions: number;
          partition_func?: unknown;
        };
        Returns: unknown;
      };
      by_range: {
        Args: {
          column_name: unknown;
          partition_interval?: unknown;
          partition_func?: unknown;
        };
        Returns: unknown;
      };
      chunk_compression_stats: {
        Args: { hypertable: unknown };
        Returns: {
          chunk_schema: unknown;
          chunk_name: unknown;
          compression_status: string;
          before_compression_table_bytes: number;
          before_compression_index_bytes: number;
          before_compression_toast_bytes: number;
          before_compression_total_bytes: number;
          after_compression_table_bytes: number;
          after_compression_index_bytes: number;
          after_compression_toast_bytes: number;
          after_compression_total_bytes: number;
          node_name: unknown;
        }[];
      };
      chunks_detailed_size: {
        Args: { hypertable: unknown };
        Returns: {
          chunk_schema: unknown;
          chunk_name: unknown;
          table_bytes: number;
          index_bytes: number;
          toast_bytes: number;
          total_bytes: number;
          node_name: unknown;
        }[];
      };
      compress_chunk: {
        Args: {
          uncompressed_chunk: unknown;
          if_not_compressed?: boolean;
          recompress?: boolean;
        };
        Returns: unknown;
      };
      create_hypertable: {
        Args:
          | {
              relation: unknown;
              dimension: unknown;
              create_default_indexes?: boolean;
              if_not_exists?: boolean;
              migrate_data?: boolean;
            }
          | {
              relation: unknown;
              time_column_name: unknown;
              partitioning_column?: unknown;
              number_partitions?: number;
              associated_schema_name?: unknown;
              associated_table_prefix?: unknown;
              chunk_time_interval?: unknown;
              create_default_indexes?: boolean;
              if_not_exists?: boolean;
              partitioning_func?: unknown;
              migrate_data?: boolean;
              chunk_target_size?: string;
              chunk_sizing_func?: unknown;
              time_partitioning_func?: unknown;
            };
        Returns: {
          hypertable_id: number;
          schema_name: unknown;
          table_name: unknown;
          created: boolean;
        }[];
      };
      current_user_has_deployment_role: {
        Args: {
          _deployment_id: string;
          _role: Database["public"]["Enums"]["deployment_role"];
        };
        Returns: boolean;
      };
      current_user_has_global_role: {
        Args: { _role: Database["public"]["Enums"]["global_role"] };
        Returns: boolean;
      };
      current_user_has_workspace_role: {
        Args: {
          _workspace_id: string;
          _role: Database["public"]["Enums"]["workspace_role"];
        };
        Returns: boolean;
      };
      current_user_owns_row: {
        Args: { _user: string };
        Returns: boolean;
      };
      current_user_owns_template: {
        Args: { _checklist_template: string };
        Returns: boolean;
      };
      decline_deployment_invitation: {
        Args: { invitation_id: string };
        Returns: undefined;
      };
      decline_workspace_invitation: {
        Args: { invitation_id: string };
        Returns: undefined;
      };
      decompress_chunk: {
        Args: { uncompressed_chunk: unknown; if_compressed?: boolean };
        Returns: unknown;
      };
      delete_deployment: {
        Args: { _deployment_id: string };
        Returns: undefined;
      };
      delete_job: {
        Args: { job_id: number };
        Returns: undefined;
      };
      delete_workspace: {
        Args: { _workspace_id: string };
        Returns: undefined;
      };
      detach_tablespace: {
        Args: {
          tablespace: unknown;
          hypertable?: unknown;
          if_attached?: boolean;
        };
        Returns: number;
      };
      detach_tablespaces: {
        Args: { hypertable: unknown };
        Returns: number;
      };
      disable_chunk_skipping: {
        Args: {
          hypertable: unknown;
          column_name: unknown;
          if_not_exists?: boolean;
        };
        Returns: {
          hypertable_id: number;
          column_name: unknown;
          disabled: boolean;
        }[];
      };
      drop_chunks: {
        Args: {
          relation: unknown;
          older_than?: unknown;
          newer_than?: unknown;
          verbose?: boolean;
          created_before?: unknown;
          created_after?: unknown;
        };
        Returns: string[];
      };
      enable_chunk_skipping: {
        Args: {
          hypertable: unknown;
          column_name: unknown;
          if_not_exists?: boolean;
        };
        Returns: {
          column_stats_id: number;
          enabled: boolean;
        }[];
      };
      export_cases_for_csv: {
        Args: {
          _deployment_ids: string[];
          _from_date: string;
          _until_date: string;
          _status: Database["public"]["Enums"]["case_status"][];
        };
        Returns: {
          created_at: string;
          resolved_at: string;
          workspace: string;
          deployment: string;
          case_name: string;
          status: Database["public"]["Enums"]["case_status"];
          tags: string;
          device: string;
          origin_workorder: string;
          solution_workorder: string;
          description: string;
          proposal: string;
          solution: string;
          assignee: string;
          created_by: string;
        }[];
      };
      export_fifo_inventory_for_csv: {
        Args: {
          _deployment_id: string;
          _from_date: string;
          _until_date: string;
        };
        Returns: {
          date: string;
          time_of_day: string;
          part_model: string;
          serial_number: string;
          material_number: string;
          action: Database["public"]["Enums"]["inventory_action"];
          order_number: string;
          quantity: number;
          single_item_price: number;
          total_price: number;
        }[];
      };
      export_maintenance_items_for_csv: {
        Args: {
          _deployment_ids: string[];
          _from_date: string;
          _until_date: string;
          _status: Database["public"]["Enums"]["maintenance_item_status"][];
          _include_device_logs: boolean;
        };
        Returns: {
          created_at: string;
          started_at: string;
          resolved_at: string;
          workspace: string;
          deployment: string;
          workorder: string;
          workorder_tags: string;
          task: string;
          description: string;
          status: Database["public"]["Enums"]["maintenance_item_status"];
          notes: string;
          device: string;
          device_ref: string;
          assignee: string;
          created_by: string;
          checklist_template: string;
        }[];
      };
      export_maintenances_for_csv: {
        Args: {
          _deployment_ids: string[];
          _from_date: string;
          _until_date: string;
          _status: Database["public"]["Enums"]["maintenance_status"][];
        };
        Returns: {
          planned_at: string;
          started_at: string;
          resolved_at: string;
          workspace: string;
          deployment: string;
          workorder: string;
          status: Database["public"]["Enums"]["maintenance_status"];
          tags: string;
          devices: string;
          notes: string;
          assignee: string;
          created_by: string;
        }[];
      };
      finish_maintenance: {
        Args: {
          _maintenance_id: string;
          _user_id: string;
          _skip_remaining_items?: boolean;
        };
        Returns: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
      };
      generate_workspace_abbreviation: {
        Args: { input_string: string };
        Returns: string;
      };
      get_bucketed_device_scalar_values: {
        Args: {
          _datafield: string;
          _deployment_ids?: string[];
          _device_ids?: string[];
          _interval?: number;
        };
        Returns: {
          date: string;
          value: number;
        }[];
      };
      get_case_logs: {
        Args: { _case_id: string };
        Returns: {
          type: Database["public"]["Enums"]["cases_history_type"];
          id: string;
          created_at: string;
          operation: Database["public"]["Enums"]["db_operation"];
          user_id: string;
          case_id: string;
          new_val: Json;
          old_val: Json;
        }[];
      };
      get_device_logs: {
        Args: { _device_id: string };
        Returns: {
          type: Database["public"]["Enums"]["devices_history_type"];
          relevant_date: string;
          workspace_id: string;
          id: string;
          assignee_id: string;
          name: string;
          description: string;
          status: string;
          maintenance: Database["public"]["Tables"]["maintenances"]["Row"];
          maintenance_items: Database["public"]["Tables"]["maintenance_items"]["Row"][];
          maintenance_item: Database["public"]["Tables"]["maintenance_items"]["Row"];
          case_item: Database["public"]["Tables"]["cases"]["Row"];
          history_event: Database["public"]["Tables"]["devices_history"]["Row"];
        }[];
      };
      get_device_logs_by_part: {
        Args: { _device_id: string; _part_id: string };
        Returns: {
          type: string;
          relevant_date: string;
          workspace_id: string;
          id: string;
          assignee_id: string;
          name: string;
          description: string;
          status: string;
          maintenance: Database["public"]["Tables"]["maintenances"]["Row"];
          maintenance_items: Database["public"]["Tables"]["maintenance_items"]["Row"][];
          maintenance_item: Database["public"]["Tables"]["maintenance_items"]["Row"];
          case_item: Database["public"]["Tables"]["cases"]["Row"];
          history_event: Database["public"]["Tables"]["devices_history"]["Row"];
        }[];
      };
      get_last_history: {
        Args: { _part_id: string };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          part_id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          seq_id: number;
          updated_at: string;
          user_id: string | null;
        };
      };
      get_maintenance_logs: {
        Args: { _maintenance_id: string };
        Returns: {
          type: Database["public"]["Enums"]["maintenances_history_type"];
          id: string;
          created_at: string;
          operation: Database["public"]["Enums"]["db_operation"];
          user_id: string;
          maintenance_id: string;
          new_val: Json;
          old_val: Json;
        }[];
      };
      get_telemetry_report: {
        Args: Record<PropertyKey, never>;
        Returns: Json;
      };
      handle_single_part: {
        Args: {
          _part: Database["public"]["Tables"]["parts"]["Row"];
          _device_id?: string;
        };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      hybrid_search: {
        Args: {
          _query_text: string;
          _query_embedding: string;
          _manufacturer_id: string;
          _match_count: number;
          _full_text_weight?: number;
          _semantic_weight?: number;
          _rrf_k?: number;
        };
        Returns: {
          bbox: number[] | null;
          chunk_number: number | null;
          content: string | null;
          created_at: string;
          document_id: string | null;
          embedding: string | null;
          fts: unknown | null;
          headings: string[] | null;
          id: string;
          label: string | null;
          original_content: string | null;
          page_number: number | null;
          updated_at: string;
          user_id: string | null;
        }[];
      };
      hypertable_approximate_detailed_size: {
        Args: { relation: unknown };
        Returns: {
          table_bytes: number;
          index_bytes: number;
          toast_bytes: number;
          total_bytes: number;
        }[];
      };
      hypertable_approximate_size: {
        Args: { hypertable: unknown };
        Returns: number;
      };
      hypertable_compression_stats: {
        Args: { hypertable: unknown };
        Returns: {
          total_chunks: number;
          number_compressed_chunks: number;
          before_compression_table_bytes: number;
          before_compression_index_bytes: number;
          before_compression_toast_bytes: number;
          before_compression_total_bytes: number;
          after_compression_table_bytes: number;
          after_compression_index_bytes: number;
          after_compression_toast_bytes: number;
          after_compression_total_bytes: number;
          node_name: unknown;
        }[];
      };
      hypertable_detailed_size: {
        Args: { hypertable: unknown };
        Returns: {
          table_bytes: number;
          index_bytes: number;
          toast_bytes: number;
          total_bytes: number;
          node_name: unknown;
        }[];
      };
      hypertable_index_size: {
        Args: { index_name: unknown };
        Returns: number;
      };
      hypertable_size: {
        Args: { hypertable: unknown };
        Returns: number;
      };
      interpolate: {
        Args:
          | {
              value: number;
              prev?: Record<string, unknown>;
              next?: Record<string, unknown>;
            }
          | {
              value: number;
              prev?: Record<string, unknown>;
              next?: Record<string, unknown>;
            }
          | {
              value: number;
              prev?: Record<string, unknown>;
              next?: Record<string, unknown>;
            }
          | {
              value: number;
              prev?: Record<string, unknown>;
              next?: Record<string, unknown>;
            }
          | {
              value: number;
              prev?: Record<string, unknown>;
              next?: Record<string, unknown>;
            };
        Returns: number;
      };
      inventory_add: {
        Args: {
          _part_id: string;
          _quantity: number;
          _note: string;
          _package_number: string;
          _package_price: number;
          _executed_at: string;
          _location_ids?: string[];
          _location_quantities?: number[];
        };
        Returns: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_assemble: {
        Args: {
          _part_id: string;
          _device_id: string;
          _quantity: number;
          _maintenance_item_id: string;
          _source_location_ids?: string[];
          _source_location_quantities?: number[];
        };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_assemble_internal: {
        Args: {
          _part: Database["public"]["Tables"]["parts"]["Row"];
          _part_id: string;
          _device_id: string;
          _quantity: number;
        };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_change: {
        Args: {
          _part_id: string;
          _quantity: number;
          _note: string;
          _package_number: string;
          _executed_at: string;
          _location_ids?: string[];
          _location_quantities?: number[];
        };
        Returns: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_disassemble: {
        Args: {
          _part_id: string;
          _quantity: number;
          _maintenance_item_id: string;
          _skip_inventory?: boolean;
          _target_location_ids?: string[];
          _target_location_quantities?: number[];
        };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_disassemble_internal: {
        Args: {
          _part: Database["public"]["Tables"]["parts"]["Row"];
          _part_id: string;
          _quantity: number;
          _skip_inventory?: boolean;
        };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_isolate: {
        Args: {
          _part_id: string;
          _new_ref: string;
          _note: string;
          _source_location_ids?: string[];
          _source_location_quantities?: number[];
          _target_location_ids?: string[];
          _target_location_quantities?: number[];
        };
        Returns: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_isolate_internal: {
        Args: {
          _part_id: string;
          _part: Database["public"]["Tables"]["parts"]["Row"];
          _new_ref: string;
        };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_move: {
        Args: {
          _part_id: string;
          _deployment_id: string;
          _quantity: number;
          _note: string;
          _package_number: string;
          _package_price: number;
          _executed_at: string;
          _source_location_ids?: string[];
          _source_location_quantities?: number[];
          _target_location_ids?: string[];
          _target_location_quantities?: number[];
        };
        Returns: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_move_internal: {
        Args: {
          _part: Database["public"]["Tables"]["parts"]["Row"];
          _part_id: string;
          _deployment_id: string;
          _quantity: number;
          _package_price: number;
          _executed_at: string;
        };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_remove: {
        Args: {
          _part_id: string;
          _quantity: number;
          _note: string;
          _package_number: string;
          _executed_at: string;
          _location_ids?: string[];
          _location_quantities?: number[];
        };
        Returns: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      inventory_undo: {
        Args: { _inventory_id: string };
        Returns: undefined;
      };
      locf: {
        Args: {
          value: unknown;
          prev?: unknown;
          treat_null_as_missing?: boolean;
        };
        Returns: unknown;
      };
      maintenance_from_checklist_template: {
        Args: {
          _checklist_template_id: string;
          _deployment_id: string;
          _title: string;
          _assignee_id?: string;
          _device_ids?: string[];
        };
        Returns: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
      };
      maintenance_from_devices: {
        Args: {
          _deployment_id: string;
          _title: string;
          _assignee_id?: string;
          _device_ids?: string[];
        };
        Returns: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
      };
      maintenance_from_maintenance: {
        Args: {
          _maintenance_id: string;
          _deployment_id: string;
          _title: string;
          _assignee_id?: string;
          _device_ids?: string[];
        };
        Returns: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
      };
      maintenance_from_maintenance_strategy: {
        Args: {
          _maintenance_strategy_id: string;
          _scheduled_at?: string;
          _title?: string;
          _assignee_id?: string;
        };
        Returns: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
      };
      move_chunk: {
        Args: {
          chunk: unknown;
          destination_tablespace: unknown;
          index_destination_tablespace?: unknown;
          reorder_index?: unknown;
          verbose?: boolean;
        };
        Returns: undefined;
      };
      move_device: {
        Args: { _device_id: string; _deployment_id: string };
        Returns: undefined;
      };
      parent_maintenance: {
        Args: { "": Database["public"]["Tables"]["maintenances"]["Row"] };
        Returns: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        }[];
      };
      random_between: {
        Args: { low: number; high: number };
        Returns: number;
      };
      remove_compression_policy: {
        Args: { hypertable: unknown; if_exists?: boolean };
        Returns: boolean;
      };
      remove_continuous_aggregate_policy: {
        Args: {
          continuous_aggregate: unknown;
          if_not_exists?: boolean;
          if_exists?: boolean;
        };
        Returns: undefined;
      };
      remove_reorder_policy: {
        Args: { hypertable: unknown; if_exists?: boolean };
        Returns: undefined;
      };
      remove_retention_policy: {
        Args: { relation: unknown; if_exists?: boolean };
        Returns: undefined;
      };
      remove_user_from_workspace: {
        Args: { _user_id: string; _workspace_id: string };
        Returns: undefined;
      };
      reorder_chunk: {
        Args: { chunk: unknown; index?: unknown; verbose?: boolean };
        Returns: undefined;
      };
      report_nerrors: {
        Args: { _from: string; _to: string; _devicesin: string[] };
        Returns: {
          day: string;
          nerrors: number;
        }[];
      };
      report_nmissions: {
        Args: { _from: string; _to: string; _devicesin: string[] };
        Returns: {
          day: string;
          nmissions: number;
        }[];
      };
      revert_part: {
        Args: {
          _part_history: Database["public"]["Tables"]["parts_history"]["Row"];
        };
        Returns: {
          archived_at: string | null;
          average_price: number | null;
          created_at: string;
          deployment_id: string;
          description: string | null;
          device_id: string | null;
          executed_at: string;
          id: string;
          part_model_id: string;
          quantity: number | null;
          ref: string | null;
          transient_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      revoke_deployment_invitation: {
        Args: { _deployment_id: string; _workspace_id: string };
        Returns: undefined;
      };
      set_adaptive_chunking: {
        Args: { hypertable: unknown; chunk_target_size: string };
        Returns: Record<string, unknown>;
      };
      set_chunk_time_interval: {
        Args: {
          hypertable: unknown;
          chunk_time_interval: unknown;
          dimension_name?: unknown;
        };
        Returns: undefined;
      };
      set_integer_now_func: {
        Args: {
          hypertable: unknown;
          integer_now_func: unknown;
          replace_if_exists?: boolean;
        };
        Returns: undefined;
      };
      set_number_partitions: {
        Args: {
          hypertable: unknown;
          number_partitions: number;
          dimension_name?: unknown;
        };
        Returns: undefined;
      };
      set_partitioning_interval: {
        Args: {
          hypertable: unknown;
          partition_interval: unknown;
          dimension_name?: unknown;
        };
        Returns: undefined;
      };
      show_chunks: {
        Args: {
          relation: unknown;
          older_than?: unknown;
          newer_than?: unknown;
          created_before?: unknown;
          created_after?: unknown;
        };
        Returns: unknown[];
      };
      show_tablespaces: {
        Args: { hypertable: unknown };
        Returns: unknown[];
      };
      skip_maintenance: {
        Args: {
          _maintenance_id: string;
          _skip_reason: string;
          _user_id: string;
        };
        Returns: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
      };
      soft_delete_device: {
        Args: { _device_id: string };
        Returns: undefined;
      };
      start_maintenance: {
        Args: { _maintenance_id: string; _user_id: string };
        Returns: {
          assignee_id: string | null;
          created_at: string;
          deployment_id: string;
          id: string;
          maintenance_strategy_id: string | null;
          notes: string;
          parent_maintenance_id: string | null;
          planned_at: string | null;
          planned_date: string | null;
          planned_until: string | null;
          public_id: string;
          resolution_time: number | null;
          resolved_at: string | null;
          scheduled_at: string | null;
          serial_id: number;
          serial_id_prefix: string;
          skip_reason: string | null;
          started_at: string | null;
          status: Database["public"]["Enums"]["maintenance_status"];
          title: string;
          updated_at: string;
          user_id: string | null;
          workspace_id: string;
        };
      };
      time_bucket: {
        Args:
          | { bucket_width: number; ts: number }
          | { bucket_width: number; ts: number }
          | { bucket_width: number; ts: number }
          | { bucket_width: number; ts: number; offset: number }
          | { bucket_width: number; ts: number; offset: number }
          | { bucket_width: number; ts: number; offset: number }
          | { bucket_width: unknown; ts: string }
          | { bucket_width: unknown; ts: string }
          | { bucket_width: unknown; ts: string }
          | { bucket_width: unknown; ts: string; offset: unknown }
          | { bucket_width: unknown; ts: string; offset: unknown }
          | { bucket_width: unknown; ts: string; offset: unknown }
          | { bucket_width: unknown; ts: string; origin: string }
          | { bucket_width: unknown; ts: string; origin: string }
          | { bucket_width: unknown; ts: string; origin: string }
          | {
              bucket_width: unknown;
              ts: string;
              timezone: string;
              origin?: string;
              offset?: unknown;
            };
        Returns: string;
      };
      time_bucket_gapfill: {
        Args:
          | {
              bucket_width: number;
              ts: number;
              start?: number;
              finish?: number;
            }
          | {
              bucket_width: number;
              ts: number;
              start?: number;
              finish?: number;
            }
          | {
              bucket_width: number;
              ts: number;
              start?: number;
              finish?: number;
            }
          | {
              bucket_width: unknown;
              ts: string;
              start?: string;
              finish?: string;
            }
          | {
              bucket_width: unknown;
              ts: string;
              start?: string;
              finish?: string;
            }
          | {
              bucket_width: unknown;
              ts: string;
              start?: string;
              finish?: string;
            }
          | {
              bucket_width: unknown;
              ts: string;
              timezone: string;
              start?: string;
              finish?: string;
            };
        Returns: string;
      };
      timescaledb_post_restore: {
        Args: Record<PropertyKey, never>;
        Returns: boolean;
      };
      timescaledb_pre_restore: {
        Args: Record<PropertyKey, never>;
        Returns: boolean;
      };
      track_part_movement: {
        Args: {
          _source_part_deployment_id: string;
          _source_part_id: string;
          _source_part_hist_id: string;
          _target_part_deployment_id: string;
          _target_part_id: string;
          _target_part_hist_id: string;
          _action: Database["public"]["Enums"]["inventory_action"];
          _quantity: number;
          _maintenance_item_id: string;
          _note?: string;
          _package_number?: string;
          _package_price?: number;
          _executed_at?: string;
          _source_location_ids?: string[];
          _source_location_quantities?: number[];
          _target_location_ids?: string[];
          _target_location_quantities?: number[];
        };
        Returns: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      track_part_operation: {
        Args: {
          _deployment_id: string;
          _part_id: string;
          _source_part_hist_id: string;
          _target_part_hist_id: string;
          _action: Database["public"]["Enums"]["inventory_action"];
          _quantity: number;
          _note?: string;
          _package_number?: string;
          _package_price?: number;
          _executed_at?: string;
          _source_location_ids?: string[];
          _source_location_quantities?: number[];
          _target_location_ids?: string[];
          _target_location_quantities?: number[];
        };
        Returns: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      update_part_quantity: {
        Args: {
          _part: Database["public"]["Tables"]["parts"]["Row"];
          _change: number;
          _quantity: number;
          _note: string;
          _package_number: string;
          _executed_at: string;
          _action: Database["public"]["Enums"]["inventory_action"];
          _package_price?: number;
          _location_ids?: string[];
          _location_quantities?: number[];
        };
        Returns: {
          action: Database["public"]["Enums"]["inventory_action"];
          created_at: string;
          deployment_id: string;
          executed_at: string;
          id: string;
          maintenance_item_id: string | null;
          note: string | null;
          package_number: string | null;
          package_price: number | null;
          part_id: string;
          quantity: number | null;
          source_part_id: string | null;
          target_part_id: string | null;
          updated_at: string;
          user_id: string | null;
        };
      };
      workspace_is_service_provider_or_manufacturer: {
        Args: { _workspace_id: string };
        Returns: boolean;
      };
    };
    Enums: {
      business_type: "device_user" | "service_provider" | "manufacturer";
      case_status: "open" | "archived" | "resolved";
      case_type: "accident" | "anomaly" | "damage" | "improvement";
      cases_history_type: "case_item" | "case_to_part" | "case_to_case_tag";
      chart_type: "line" | "area" | "bar";
      chart_value_aggregation: "single" | "sum";
      connection_status: "pending" | "online" | "offline";
      db_operation: "INSERT" | "UPDATE" | "DELETE";
      deployment_role: "admin" | "technician" | "viewer" | "none" | "end_customer";
      device_order_status: "pending" | "started" | "finished" | "aborted" | "cancelled" | "missing";
      devices_history_type: "case_item" | "maintenance_item" | "maintenance" | "history_event";
      global_role: "admin" | "none";
      inventory_action: "add" | "remove" | "change" | "move" | "isolate" | "assemble" | "disassemble";
      inventory_location_type: "source" | "target";
      maintenance_item_status: "pending" | "started" | "skipped" | "completed" | "failed";
      maintenance_items_to_parts_type: "proposed" | "executed";
      maintenance_status: "scheduled" | "started" | "finished" | "skipped";
      maintenances_history_type:
        | "maintenance"
        | "maintenance_to_device"
        | "maintenance_approval"
        | "maintenance_to_maintenance_tag";
      part_usage_type: "disassembly" | "assembly" | "cleaning" | "repair" | "calibration" | "update" | "unspecified";
      trigger_metric_type: "N_MISSIONS" | "N_ERRORS";
      workspace_role: "admin" | "viewer" | "associate" | "none";
    };
    CompositeTypes: {
      fifo_inventory_record: {
        part_id: string | null;
        in_id: string | null;
        out_id: string | null;
        in_executed_at: string | null;
        in_action: Database["public"]["Enums"]["inventory_action"] | null;
        unit_value: number | null;
        out_unit_value: number | null;
        out_executed_at: string | null;
        out_action: Database["public"]["Enums"]["inventory_action"] | null;
        taken_quantity: number | null;
        remaining_quantity: number | null;
      };
    };
  };
  storage: {
    Tables: {
      buckets: {
        Row: {
          allowed_mime_types: string[] | null;
          avif_autodetection: boolean | null;
          created_at: string | null;
          file_size_limit: number | null;
          id: string;
          name: string;
          owner: string | null;
          owner_id: string | null;
          public: boolean | null;
          updated_at: string | null;
        };
        Insert: {
          allowed_mime_types?: string[] | null;
          avif_autodetection?: boolean | null;
          created_at?: string | null;
          file_size_limit?: number | null;
          id: string;
          name: string;
          owner?: string | null;
          owner_id?: string | null;
          public?: boolean | null;
          updated_at?: string | null;
        };
        Update: {
          allowed_mime_types?: string[] | null;
          avif_autodetection?: boolean | null;
          created_at?: string | null;
          file_size_limit?: number | null;
          id?: string;
          name?: string;
          owner?: string | null;
          owner_id?: string | null;
          public?: boolean | null;
          updated_at?: string | null;
        };
        Relationships: [];
      };
      migrations: {
        Row: {
          executed_at: string | null;
          hash: string;
          id: number;
          name: string;
        };
        Insert: {
          executed_at?: string | null;
          hash: string;
          id: number;
          name: string;
        };
        Update: {
          executed_at?: string | null;
          hash?: string;
          id?: number;
          name?: string;
        };
        Relationships: [];
      };
      objects: {
        Row: {
          bucket_id: string | null;
          created_at: string | null;
          id: string;
          last_accessed_at: string | null;
          level: number | null;
          metadata: Json | null;
          name: string | null;
          owner: string | null;
          owner_id: string | null;
          path_tokens: string[] | null;
          updated_at: string | null;
          user_metadata: Json | null;
          version: string | null;
        };
        Insert: {
          bucket_id?: string | null;
          created_at?: string | null;
          id?: string;
          last_accessed_at?: string | null;
          level?: number | null;
          metadata?: Json | null;
          name?: string | null;
          owner?: string | null;
          owner_id?: string | null;
          path_tokens?: string[] | null;
          updated_at?: string | null;
          user_metadata?: Json | null;
          version?: string | null;
        };
        Update: {
          bucket_id?: string | null;
          created_at?: string | null;
          id?: string;
          last_accessed_at?: string | null;
          level?: number | null;
          metadata?: Json | null;
          name?: string | null;
          owner?: string | null;
          owner_id?: string | null;
          path_tokens?: string[] | null;
          updated_at?: string | null;
          user_metadata?: Json | null;
          version?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "objects_bucketId_fkey";
            columns: ["bucket_id"];
            isOneToOne: false;
            referencedRelation: "buckets";
            referencedColumns: ["id"];
          },
        ];
      };
      prefixes: {
        Row: {
          bucket_id: string;
          created_at: string | null;
          level: number;
          name: string;
          updated_at: string | null;
        };
        Insert: {
          bucket_id: string;
          created_at?: string | null;
          level?: number;
          name: string;
          updated_at?: string | null;
        };
        Update: {
          bucket_id?: string;
          created_at?: string | null;
          level?: number;
          name?: string;
          updated_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "prefixes_bucketId_fkey";
            columns: ["bucket_id"];
            isOneToOne: false;
            referencedRelation: "buckets";
            referencedColumns: ["id"];
          },
        ];
      };
      s3_multipart_uploads: {
        Row: {
          bucket_id: string;
          created_at: string;
          id: string;
          in_progress_size: number;
          key: string;
          owner_id: string | null;
          upload_signature: string;
          user_metadata: Json | null;
          version: string;
        };
        Insert: {
          bucket_id: string;
          created_at?: string;
          id: string;
          in_progress_size?: number;
          key: string;
          owner_id?: string | null;
          upload_signature: string;
          user_metadata?: Json | null;
          version: string;
        };
        Update: {
          bucket_id?: string;
          created_at?: string;
          id?: string;
          in_progress_size?: number;
          key?: string;
          owner_id?: string | null;
          upload_signature?: string;
          user_metadata?: Json | null;
          version?: string;
        };
        Relationships: [
          {
            foreignKeyName: "s3_multipart_uploads_bucket_id_fkey";
            columns: ["bucket_id"];
            isOneToOne: false;
            referencedRelation: "buckets";
            referencedColumns: ["id"];
          },
        ];
      };
      s3_multipart_uploads_parts: {
        Row: {
          bucket_id: string;
          created_at: string;
          etag: string;
          id: string;
          key: string;
          owner_id: string | null;
          part_number: number;
          size: number;
          upload_id: string;
          version: string;
        };
        Insert: {
          bucket_id: string;
          created_at?: string;
          etag: string;
          id?: string;
          key: string;
          owner_id?: string | null;
          part_number: number;
          size?: number;
          upload_id: string;
          version: string;
        };
        Update: {
          bucket_id?: string;
          created_at?: string;
          etag?: string;
          id?: string;
          key?: string;
          owner_id?: string | null;
          part_number?: number;
          size?: number;
          upload_id?: string;
          version?: string;
        };
        Relationships: [
          {
            foreignKeyName: "s3_multipart_uploads_parts_bucket_id_fkey";
            columns: ["bucket_id"];
            isOneToOne: false;
            referencedRelation: "buckets";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "s3_multipart_uploads_parts_upload_id_fkey";
            columns: ["upload_id"];
            isOneToOne: false;
            referencedRelation: "s3_multipart_uploads";
            referencedColumns: ["id"];
          },
        ];
      };
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      add_prefixes: {
        Args: { _bucket_id: string; _name: string };
        Returns: undefined;
      };
      can_insert_object: {
        Args: { bucketid: string; name: string; owner: string; metadata: Json };
        Returns: undefined;
      };
      delete_prefix: {
        Args: { _bucket_id: string; _name: string };
        Returns: boolean;
      };
      extension: {
        Args: { name: string };
        Returns: string;
      };
      filename: {
        Args: { name: string };
        Returns: string;
      };
      foldername: {
        Args: { name: string };
        Returns: string[];
      };
      get_level: {
        Args: { name: string };
        Returns: number;
      };
      get_prefix: {
        Args: { name: string };
        Returns: string;
      };
      get_prefixes: {
        Args: { name: string };
        Returns: string[];
      };
      get_size_by_bucket: {
        Args: Record<PropertyKey, never>;
        Returns: {
          size: number;
          bucket_id: string;
        }[];
      };
      list_multipart_uploads_with_delimiter: {
        Args: {
          bucket_id: string;
          prefix_param: string;
          delimiter_param: string;
          max_keys?: number;
          next_key_token?: string;
          next_upload_token?: string;
        };
        Returns: {
          key: string;
          id: string;
          created_at: string;
        }[];
      };
      list_objects_with_delimiter: {
        Args: {
          bucket_id: string;
          prefix_param: string;
          delimiter_param: string;
          max_keys?: number;
          start_after?: string;
          next_token?: string;
        };
        Returns: {
          name: string;
          id: string;
          metadata: Json;
          updated_at: string;
        }[];
      };
      operation: {
        Args: Record<PropertyKey, never>;
        Returns: string;
      };
      search: {
        Args: {
          prefix: string;
          bucketname: string;
          limits?: number;
          levels?: number;
          offsets?: number;
          search?: string;
          sortcolumn?: string;
          sortorder?: string;
        };
        Returns: {
          name: string;
          id: string;
          updated_at: string;
          created_at: string;
          last_accessed_at: string;
          metadata: Json;
        }[];
      };
      search_legacy_v1: {
        Args: {
          prefix: string;
          bucketname: string;
          limits?: number;
          levels?: number;
          offsets?: number;
          search?: string;
          sortcolumn?: string;
          sortorder?: string;
        };
        Returns: {
          name: string;
          id: string;
          updated_at: string;
          created_at: string;
          last_accessed_at: string;
          metadata: Json;
        }[];
      };
      search_v1_optimised: {
        Args: {
          prefix: string;
          bucketname: string;
          limits?: number;
          levels?: number;
          offsets?: number;
          search?: string;
          sortcolumn?: string;
          sortorder?: string;
        };
        Returns: {
          name: string;
          id: string;
          updated_at: string;
          created_at: string;
          last_accessed_at: string;
          metadata: Json;
        }[];
      };
      search_v2: {
        Args: {
          prefix: string;
          bucket_name: string;
          limits?: number;
          levels?: number;
          start_after?: string;
        };
        Returns: {
          key: string;
          name: string;
          id: string;
          updated_at: string;
          created_at: string;
          metadata: Json;
        }[];
      };
    };
    Enums: {
      [_ in never]: never;
    };
    CompositeTypes: {
      [_ in never]: never;
    };
  };
};

type DefaultSchema = Database[Extract<keyof Database, "public">];

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof Database },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof Database;
  }
    ? keyof (Database[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        Database[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends { schema: keyof Database }
  ? (Database[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      Database[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R;
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] & DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R;
      }
      ? R
      : never
    : never;

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"] | { schema: keyof Database },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof Database;
  }
    ? keyof Database[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends { schema: keyof Database }
  ? Database[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I;
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I;
      }
      ? I
      : never
    : never;

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"] | { schema: keyof Database },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof Database;
  }
    ? keyof Database[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends { schema: keyof Database }
  ? Database[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U;
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U;
      }
      ? U
      : never
    : never;

export type Enums<
  DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"] | { schema: keyof Database },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof Database;
  }
    ? keyof Database[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends { schema: keyof Database }
  ? Database[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never;

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"] | { schema: keyof Database },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof Database;
  }
    ? keyof Database[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never = never,
> = PublicCompositeTypeNameOrOptions extends { schema: keyof Database }
  ? Database[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never;

export const Constants = {
  graphql_public: {
    Enums: {},
  },
  public: {
    Enums: {
      business_type: ["device_user", "service_provider", "manufacturer"],
      case_status: ["open", "archived", "resolved"],
      case_type: ["accident", "anomaly", "damage", "improvement"],
      cases_history_type: ["case_item", "case_to_part", "case_to_case_tag"],
      chart_type: ["line", "area", "bar"],
      chart_value_aggregation: ["single", "sum"],
      connection_status: ["pending", "online", "offline"],
      db_operation: ["INSERT", "UPDATE", "DELETE"],
      deployment_role: ["admin", "technician", "viewer", "none", "end_customer"],
      device_order_status: ["pending", "started", "finished", "aborted", "cancelled", "missing"],
      devices_history_type: ["case_item", "maintenance_item", "maintenance", "history_event"],
      global_role: ["admin", "none"],
      inventory_action: ["add", "remove", "change", "move", "isolate", "assemble", "disassemble"],
      inventory_location_type: ["source", "target"],
      maintenance_item_status: ["pending", "started", "skipped", "completed", "failed"],
      maintenance_items_to_parts_type: ["proposed", "executed"],
      maintenance_status: ["scheduled", "started", "finished", "skipped"],
      maintenances_history_type: [
        "maintenance",
        "maintenance_to_device",
        "maintenance_approval",
        "maintenance_to_maintenance_tag",
      ],
      part_usage_type: ["disassembly", "assembly", "cleaning", "repair", "calibration", "update", "unspecified"],
      trigger_metric_type: ["N_MISSIONS", "N_ERRORS"],
      workspace_role: ["admin", "viewer", "associate", "none"],
    },
  },
  storage: {
    Enums: {},
  },
} as const;

// Schema: graphql_public
// Functions
export type ArgsGraphql = Database["graphql_public"]["Functions"]["graphql"]["Args"];
export type ReturnTypeGraphql = Database["graphql_public"]["Functions"]["graphql"]["Returns"];

// Schema: public
// Enums
export enum BusinessType {
  device_user = "device_user",
  service_provider = "service_provider",
  manufacturer = "manufacturer",
}

export enum CaseStatus {
  open = "open",
  archived = "archived",
  resolved = "resolved",
}

export enum CaseType {
  accident = "accident",
  anomaly = "anomaly",
  damage = "damage",
  improvement = "improvement",
}

export enum CaseHistoryType {
  case_item = "case_item",
  case_to_part = "case_to_part",
  case_to_case_tag = "case_to_case_tag",
}

export enum ChartType {
  line = "line",
  area = "area",
  bar = "bar",
}

export enum ChartValueAggregation {
  single = "single",
  sum = "sum",
}

export enum ConnectionStatus {
  pending = "pending",
  online = "online",
  offline = "offline",
}

export enum DbOperation {
  INSERT = "INSERT",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
}

export enum DeploymentRole {
  admin = "admin",
  technician = "technician",
  viewer = "viewer",
  none = "none",
  end_customer = "end_customer",
}

export enum DeviceOrderStatus {
  pending = "pending",
  started = "started",
  finished = "finished",
  aborted = "aborted",
  cancelled = "cancelled",
  missing = "missing",
}

export enum DeviceHistoryType {
  case_item = "case_item",
  maintenance_item = "maintenance_item",
  maintenance = "maintenance",
  history_event = "history_event",
}

export enum GlobalRole {
  admin = "admin",
  none = "none",
}

export enum InventoryAction {
  add = "add",
  remove = "remove",
  change = "change",
  move = "move",
  isolate = "isolate",
  assemble = "assemble",
  disassemble = "disassemble",
}

export enum InventoryLocationType {
  source = "source",
  target = "target",
}

export enum MaintenanceItemStatus {
  pending = "pending",
  started = "started",
  skipped = "skipped",
  completed = "completed",
  failed = "failed",
}

export enum MaintenanceItemToPartType {
  proposed = "proposed",
  executed = "executed",
}

export enum MaintenanceStatus {
  scheduled = "scheduled",
  started = "started",
  finished = "finished",
  skipped = "skipped",
}

export enum MaintenanceHistoryType {
  maintenance = "maintenance",
  maintenance_to_device = "maintenance_to_device",
  maintenance_approval = "maintenance_approval",
  maintenance_to_maintenance_tag = "maintenance_to_maintenance_tag",
}

export enum PartUsageType {
  disassembly = "disassembly",
  assembly = "assembly",
  cleaning = "cleaning",
  repair = "repair",
  calibration = "calibration",
  update = "update",
  unspecified = "unspecified",
}

export enum TriggerMetricType {
  N_MISSIONS = "N_MISSIONS",
  N_ERRORS = "N_ERRORS",
}

export enum WorkspaceRole {
  admin = "admin",
  viewer = "viewer",
  associate = "associate",
  none = "none",
}

// Tables
export type CaseFile = Database["public"]["Tables"]["case_files"]["Row"];
export type InsertCaseFile = Database["public"]["Tables"]["case_files"]["Insert"];
export type UpdateCaseFile = Database["public"]["Tables"]["case_files"]["Update"];

export type CaseTag = Database["public"]["Tables"]["case_tags"]["Row"];
export type InsertCaseTag = Database["public"]["Tables"]["case_tags"]["Insert"];
export type UpdateCaseTag = Database["public"]["Tables"]["case_tags"]["Update"];

export type Case = Database["public"]["Tables"]["cases"]["Row"];
export type InsertCase = Database["public"]["Tables"]["cases"]["Insert"];
export type UpdateCase = Database["public"]["Tables"]["cases"]["Update"];

export type CaseHistory = Database["public"]["Tables"]["cases_history"]["Row"];
export type InsertCaseHistory = Database["public"]["Tables"]["cases_history"]["Insert"];
export type UpdateCaseHistory = Database["public"]["Tables"]["cases_history"]["Update"];

export type CaseToCaseTag = Database["public"]["Tables"]["cases_to_case_tags"]["Row"];
export type InsertCaseToCaseTag = Database["public"]["Tables"]["cases_to_case_tags"]["Insert"];
export type UpdateCaseToCaseTag = Database["public"]["Tables"]["cases_to_case_tags"]["Update"];

export type CaseToCaseTagHistory = Database["public"]["Tables"]["cases_to_case_tags_history"]["Row"];
export type InsertCaseToCaseTagHistory = Database["public"]["Tables"]["cases_to_case_tags_history"]["Insert"];
export type UpdateCaseToCaseTagHistory = Database["public"]["Tables"]["cases_to_case_tags_history"]["Update"];

export type CaseToPart = Database["public"]["Tables"]["cases_to_parts"]["Row"];
export type InsertCaseToPart = Database["public"]["Tables"]["cases_to_parts"]["Insert"];
export type UpdateCaseToPart = Database["public"]["Tables"]["cases_to_parts"]["Update"];

export type CaseToPartHistory = Database["public"]["Tables"]["cases_to_parts_history"]["Row"];
export type InsertCaseToPartHistory = Database["public"]["Tables"]["cases_to_parts_history"]["Insert"];
export type UpdateCaseToPartHistory = Database["public"]["Tables"]["cases_to_parts_history"]["Update"];

export type ChecklistTemplateItemFile = Database["public"]["Tables"]["checklist_template_item_files"]["Row"];
export type InsertChecklistTemplateItemFile = Database["public"]["Tables"]["checklist_template_item_files"]["Insert"];
export type UpdateChecklistTemplateItemFile = Database["public"]["Tables"]["checklist_template_item_files"]["Update"];

export type ChecklistTemplateItem = Database["public"]["Tables"]["checklist_template_items"]["Row"];
export type InsertChecklistTemplateItem = Database["public"]["Tables"]["checklist_template_items"]["Insert"];
export type UpdateChecklistTemplateItem = Database["public"]["Tables"]["checklist_template_items"]["Update"];

export type ChecklistTemplateItemToPart = Database["public"]["Tables"]["checklist_template_items_to_parts"]["Row"];
export type InsertChecklistTemplateItemToPart =
  Database["public"]["Tables"]["checklist_template_items_to_parts"]["Insert"];
export type UpdateChecklistTemplateItemToPart =
  Database["public"]["Tables"]["checklist_template_items_to_parts"]["Update"];

export type ChecklistTemplate = Database["public"]["Tables"]["checklist_templates"]["Row"];
export type InsertChecklistTemplate = Database["public"]["Tables"]["checklist_templates"]["Insert"];
export type UpdateChecklistTemplate = Database["public"]["Tables"]["checklist_templates"]["Update"];

export type ChecklistTemplateToWorkspace = Database["public"]["Tables"]["checklist_templates_to_workspaces"]["Row"];
export type InsertChecklistTemplateToWorkspace =
  Database["public"]["Tables"]["checklist_templates_to_workspaces"]["Insert"];
export type UpdateChecklistTemplateToWorkspace =
  Database["public"]["Tables"]["checklist_templates_to_workspaces"]["Update"];

export type ChunkedDocument = Database["public"]["Tables"]["chunked_documents"]["Row"];
export type InsertChunkedDocument = Database["public"]["Tables"]["chunked_documents"]["Insert"];
export type UpdateChunkedDocument = Database["public"]["Tables"]["chunked_documents"]["Update"];

export type Connection = Database["public"]["Tables"]["connections"]["Row"];
export type InsertConnection = Database["public"]["Tables"]["connections"]["Insert"];
export type UpdateConnection = Database["public"]["Tables"]["connections"]["Update"];

export type ConnectionToDevice = Database["public"]["Tables"]["connections_to_devices"]["Row"];
export type InsertConnectionToDevice = Database["public"]["Tables"]["connections_to_devices"]["Insert"];
export type UpdateConnectionToDevice = Database["public"]["Tables"]["connections_to_devices"]["Update"];

export type DeploymentInvitation = Database["public"]["Tables"]["deployment_invitations"]["Row"];
export type InsertDeploymentInvitation = Database["public"]["Tables"]["deployment_invitations"]["Insert"];
export type UpdateDeploymentInvitation = Database["public"]["Tables"]["deployment_invitations"]["Update"];

export type DeploymentSetting = Database["public"]["Tables"]["deployment_settings"]["Row"];
export type InsertDeploymentSetting = Database["public"]["Tables"]["deployment_settings"]["Insert"];
export type UpdateDeploymentSetting = Database["public"]["Tables"]["deployment_settings"]["Update"];

export type Deployment = Database["public"]["Tables"]["deployments"]["Row"];
export type InsertDeployment = Database["public"]["Tables"]["deployments"]["Insert"];
export type UpdateDeployment = Database["public"]["Tables"]["deployments"]["Update"];

export type DeploymentToWorkspace = Database["public"]["Tables"]["deployments_to_workspaces"]["Row"];
export type InsertDeploymentToWorkspace = Database["public"]["Tables"]["deployments_to_workspaces"]["Insert"];
export type UpdateDeploymentToWorkspace = Database["public"]["Tables"]["deployments_to_workspaces"]["Update"];

export type DeviceError = Database["public"]["Tables"]["device_errors"]["Row"];
export type InsertDeviceError = Database["public"]["Tables"]["device_errors"]["Insert"];
export type UpdateDeviceError = Database["public"]["Tables"]["device_errors"]["Update"];

export type DeviceFile = Database["public"]["Tables"]["device_files"]["Row"];
export type InsertDeviceFile = Database["public"]["Tables"]["device_files"]["Insert"];
export type UpdateDeviceFile = Database["public"]["Tables"]["device_files"]["Update"];

export type DeviceInsightChart = Database["public"]["Tables"]["device_insights_charts"]["Row"];
export type InsertDeviceInsightChart = Database["public"]["Tables"]["device_insights_charts"]["Insert"];
export type UpdateDeviceInsightChart = Database["public"]["Tables"]["device_insights_charts"]["Update"];

export type DeviceModel = Database["public"]["Tables"]["device_models"]["Row"];
export type InsertDeviceModel = Database["public"]["Tables"]["device_models"]["Insert"];
export type UpdateDeviceModel = Database["public"]["Tables"]["device_models"]["Update"];

export type DeviceOrder = Database["public"]["Tables"]["device_orders"]["Row"];
export type InsertDeviceOrder = Database["public"]["Tables"]["device_orders"]["Insert"];
export type UpdateDeviceOrder = Database["public"]["Tables"]["device_orders"]["Update"];

export type DeviceScalarValue = Database["public"]["Tables"]["device_scalar_values"]["Row"];
export type InsertDeviceScalarValue = Database["public"]["Tables"]["device_scalar_values"]["Insert"];
export type UpdateDeviceScalarValue = Database["public"]["Tables"]["device_scalar_values"]["Update"];

export type DeviceTag = Database["public"]["Tables"]["device_tags"]["Row"];
export type InsertDeviceTag = Database["public"]["Tables"]["device_tags"]["Insert"];
export type UpdateDeviceTag = Database["public"]["Tables"]["device_tags"]["Update"];

export type Device = Database["public"]["Tables"]["devices"]["Row"];
export type InsertDevice = Database["public"]["Tables"]["devices"]["Insert"];
export type UpdateDevice = Database["public"]["Tables"]["devices"]["Update"];

export type DeviceHistory = Database["public"]["Tables"]["devices_history"]["Row"];
export type InsertDeviceHistory = Database["public"]["Tables"]["devices_history"]["Insert"];
export type UpdateDeviceHistory = Database["public"]["Tables"]["devices_history"]["Update"];

export type DeviceToDeviceTag = Database["public"]["Tables"]["devices_to_device_tags"]["Row"];
export type InsertDeviceToDeviceTag = Database["public"]["Tables"]["devices_to_device_tags"]["Insert"];
export type UpdateDeviceToDeviceTag = Database["public"]["Tables"]["devices_to_device_tags"]["Update"];

export type Document = Database["public"]["Tables"]["documents"]["Row"];
export type InsertDocument = Database["public"]["Tables"]["documents"]["Insert"];
export type UpdateDocument = Database["public"]["Tables"]["documents"]["Update"];

export type GopgMigration = Database["public"]["Tables"]["gopg_migrations"]["Row"];
export type InsertGopgMigration = Database["public"]["Tables"]["gopg_migrations"]["Insert"];
export type UpdateGopgMigration = Database["public"]["Tables"]["gopg_migrations"]["Update"];

export type MaintenanceApprovalFile = Database["public"]["Tables"]["maintenance_approval_files"]["Row"];
export type InsertMaintenanceApprovalFile = Database["public"]["Tables"]["maintenance_approval_files"]["Insert"];
export type UpdateMaintenanceApprovalFile = Database["public"]["Tables"]["maintenance_approval_files"]["Update"];

export type MaintenanceApproval = Database["public"]["Tables"]["maintenance_approvals"]["Row"];
export type InsertMaintenanceApproval = Database["public"]["Tables"]["maintenance_approvals"]["Insert"];
export type UpdateMaintenanceApproval = Database["public"]["Tables"]["maintenance_approvals"]["Update"];

export type MaintenanceApprovalHistory = Database["public"]["Tables"]["maintenance_approvals_history"]["Row"];
export type InsertMaintenanceApprovalHistory = Database["public"]["Tables"]["maintenance_approvals_history"]["Insert"];
export type UpdateMaintenanceApprovalHistory = Database["public"]["Tables"]["maintenance_approvals_history"]["Update"];

export type MaintenanceItemFile = Database["public"]["Tables"]["maintenance_item_files"]["Row"];
export type InsertMaintenanceItemFile = Database["public"]["Tables"]["maintenance_item_files"]["Insert"];
export type UpdateMaintenanceItemFile = Database["public"]["Tables"]["maintenance_item_files"]["Update"];

export type MaintenanceItemTemplateFile = Database["public"]["Tables"]["maintenance_item_template_files"]["Row"];
export type InsertMaintenanceItemTemplateFile =
  Database["public"]["Tables"]["maintenance_item_template_files"]["Insert"];
export type UpdateMaintenanceItemTemplateFile =
  Database["public"]["Tables"]["maintenance_item_template_files"]["Update"];

export type MaintenanceItem = Database["public"]["Tables"]["maintenance_items"]["Row"];
export type InsertMaintenanceItem = Database["public"]["Tables"]["maintenance_items"]["Insert"];
export type UpdateMaintenanceItem = Database["public"]["Tables"]["maintenance_items"]["Update"];

export type MaintenanceItemToPart = Database["public"]["Tables"]["maintenance_items_to_parts"]["Row"];
export type InsertMaintenanceItemToPart = Database["public"]["Tables"]["maintenance_items_to_parts"]["Insert"];
export type UpdateMaintenanceItemToPart = Database["public"]["Tables"]["maintenance_items_to_parts"]["Update"];

export type MaintenanceItemToPartToPartLocation =
  Database["public"]["Tables"]["maintenance_items_to_parts_to_part_locations"]["Row"];
export type InsertMaintenanceItemToPartToPartLocation =
  Database["public"]["Tables"]["maintenance_items_to_parts_to_part_locations"]["Insert"];
export type UpdateMaintenanceItemToPartToPartLocation =
  Database["public"]["Tables"]["maintenance_items_to_parts_to_part_locations"]["Update"];

export type MaintenanceStrategy = Database["public"]["Tables"]["maintenance_strategies"]["Row"];
export type InsertMaintenanceStrategy = Database["public"]["Tables"]["maintenance_strategies"]["Insert"];
export type UpdateMaintenanceStrategy = Database["public"]["Tables"]["maintenance_strategies"]["Update"];

export type MaintenanceStrategyToDevice = Database["public"]["Tables"]["maintenance_strategies_to_devices"]["Row"];
export type InsertMaintenanceStrategyToDevice =
  Database["public"]["Tables"]["maintenance_strategies_to_devices"]["Insert"];
export type UpdateMaintenanceStrategyToDevice =
  Database["public"]["Tables"]["maintenance_strategies_to_devices"]["Update"];

export type MaintenanceStrategyInterval = Database["public"]["Tables"]["maintenance_strategy_intervals"]["Row"];
export type InsertMaintenanceStrategyInterval =
  Database["public"]["Tables"]["maintenance_strategy_intervals"]["Insert"];
export type UpdateMaintenanceStrategyInterval =
  Database["public"]["Tables"]["maintenance_strategy_intervals"]["Update"];

export type MaintenanceStrategyTrigger = Database["public"]["Tables"]["maintenance_strategy_triggers"]["Row"];
export type InsertMaintenanceStrategyTrigger = Database["public"]["Tables"]["maintenance_strategy_triggers"]["Insert"];
export type UpdateMaintenanceStrategyTrigger = Database["public"]["Tables"]["maintenance_strategy_triggers"]["Update"];

export type MaintenanceTag = Database["public"]["Tables"]["maintenance_tags"]["Row"];
export type InsertMaintenanceTag = Database["public"]["Tables"]["maintenance_tags"]["Insert"];
export type UpdateMaintenanceTag = Database["public"]["Tables"]["maintenance_tags"]["Update"];

export type MaintenanceTriggerToDeviceMetric =
  Database["public"]["Tables"]["maintenance_trigger_to_device_metrics"]["Row"];
export type InsertMaintenanceTriggerToDeviceMetric =
  Database["public"]["Tables"]["maintenance_trigger_to_device_metrics"]["Insert"];
export type UpdateMaintenanceTriggerToDeviceMetric =
  Database["public"]["Tables"]["maintenance_trigger_to_device_metrics"]["Update"];

export type Maintenance = Database["public"]["Tables"]["maintenances"]["Row"];
export type InsertMaintenance = Database["public"]["Tables"]["maintenances"]["Insert"];
export type UpdateMaintenance = Database["public"]["Tables"]["maintenances"]["Update"];

export type MaintenanceHistory = Database["public"]["Tables"]["maintenances_history"]["Row"];
export type InsertMaintenanceHistory = Database["public"]["Tables"]["maintenances_history"]["Insert"];
export type UpdateMaintenanceHistory = Database["public"]["Tables"]["maintenances_history"]["Update"];

export type MaintenanceToDevice = Database["public"]["Tables"]["maintenances_to_devices"]["Row"];
export type InsertMaintenanceToDevice = Database["public"]["Tables"]["maintenances_to_devices"]["Insert"];
export type UpdateMaintenanceToDevice = Database["public"]["Tables"]["maintenances_to_devices"]["Update"];

export type MaintenanceToDeviceHistory = Database["public"]["Tables"]["maintenances_to_devices_history"]["Row"];
export type InsertMaintenanceToDeviceHistory =
  Database["public"]["Tables"]["maintenances_to_devices_history"]["Insert"];
export type UpdateMaintenanceToDeviceHistory =
  Database["public"]["Tables"]["maintenances_to_devices_history"]["Update"];

export type MaintenanceToMaintenanceTag = Database["public"]["Tables"]["maintenances_to_maintenance_tags"]["Row"];
export type InsertMaintenanceToMaintenanceTag =
  Database["public"]["Tables"]["maintenances_to_maintenance_tags"]["Insert"];
export type UpdateMaintenanceToMaintenanceTag =
  Database["public"]["Tables"]["maintenances_to_maintenance_tags"]["Update"];

export type MaintenanceToMaintenanceTagHistory =
  Database["public"]["Tables"]["maintenances_to_maintenance_tags_history"]["Row"];
export type InsertMaintenanceToMaintenanceTagHistory =
  Database["public"]["Tables"]["maintenances_to_maintenance_tags_history"]["Insert"];
export type UpdateMaintenanceToMaintenanceTagHistory =
  Database["public"]["Tables"]["maintenances_to_maintenance_tags_history"]["Update"];

export type Manufacturer = Database["public"]["Tables"]["manufacturers"]["Row"];
export type InsertManufacturer = Database["public"]["Tables"]["manufacturers"]["Insert"];
export type UpdateManufacturer = Database["public"]["Tables"]["manufacturers"]["Update"];

export type PartCategory = Database["public"]["Tables"]["part_categories"]["Row"];
export type InsertPartCategory = Database["public"]["Tables"]["part_categories"]["Insert"];
export type UpdatePartCategory = Database["public"]["Tables"]["part_categories"]["Update"];

export type PartInventory = Database["public"]["Tables"]["part_inventories"]["Row"];
export type InsertPartInventory = Database["public"]["Tables"]["part_inventories"]["Insert"];
export type UpdatePartInventory = Database["public"]["Tables"]["part_inventories"]["Update"];

export type PartInventoryToLocation = Database["public"]["Tables"]["part_inventories_to_locations"]["Row"];
export type InsertPartInventoryToLocation = Database["public"]["Tables"]["part_inventories_to_locations"]["Insert"];
export type UpdatePartInventoryToLocation = Database["public"]["Tables"]["part_inventories_to_locations"]["Update"];

export type PartInventoryFile = Database["public"]["Tables"]["part_inventory_files"]["Row"];
export type InsertPartInventoryFile = Database["public"]["Tables"]["part_inventory_files"]["Insert"];
export type UpdatePartInventoryFile = Database["public"]["Tables"]["part_inventory_files"]["Update"];

export type PartLocationTemplate = Database["public"]["Tables"]["part_location_templates"]["Row"];
export type InsertPartLocationTemplate = Database["public"]["Tables"]["part_location_templates"]["Insert"];
export type UpdatePartLocationTemplate = Database["public"]["Tables"]["part_location_templates"]["Update"];

export type PartLocation = Database["public"]["Tables"]["part_locations"]["Row"];
export type InsertPartLocation = Database["public"]["Tables"]["part_locations"]["Insert"];
export type UpdatePartLocation = Database["public"]["Tables"]["part_locations"]["Update"];

export type PartManufacturer = Database["public"]["Tables"]["part_manufacturers"]["Row"];
export type InsertPartManufacturer = Database["public"]["Tables"]["part_manufacturers"]["Insert"];
export type UpdatePartManufacturer = Database["public"]["Tables"]["part_manufacturers"]["Update"];

export type PartModelConstraint = Database["public"]["Tables"]["part_model_constraints"]["Row"];
export type InsertPartModelConstraint = Database["public"]["Tables"]["part_model_constraints"]["Insert"];
export type UpdatePartModelConstraint = Database["public"]["Tables"]["part_model_constraints"]["Update"];

export type PartModel = Database["public"]["Tables"]["part_models"]["Row"];
export type InsertPartModel = Database["public"]["Tables"]["part_models"]["Insert"];
export type UpdatePartModel = Database["public"]["Tables"]["part_models"]["Update"];

export type PartToPartLocation = Database["public"]["Tables"]["part_to_part_locations"]["Row"];
export type InsertPartToPartLocation = Database["public"]["Tables"]["part_to_part_locations"]["Insert"];
export type UpdatePartToPartLocation = Database["public"]["Tables"]["part_to_part_locations"]["Update"];

export type Part = Database["public"]["Tables"]["parts"]["Row"];
export type InsertPart = Database["public"]["Tables"]["parts"]["Insert"];
export type UpdatePart = Database["public"]["Tables"]["parts"]["Update"];

export type PartHistory = Database["public"]["Tables"]["parts_history"]["Row"];
export type InsertPartHistory = Database["public"]["Tables"]["parts_history"]["Insert"];
export type UpdatePartHistory = Database["public"]["Tables"]["parts_history"]["Update"];

export type PhysicalUnit = Database["public"]["Tables"]["physical_units"]["Row"];
export type InsertPhysicalUnit = Database["public"]["Tables"]["physical_units"]["Insert"];
export type UpdatePhysicalUnit = Database["public"]["Tables"]["physical_units"]["Update"];

export type UserProfile = Database["public"]["Tables"]["user_profiles"]["Row"];
export type InsertUserProfile = Database["public"]["Tables"]["user_profiles"]["Insert"];
export type UpdateUserProfile = Database["public"]["Tables"]["user_profiles"]["Update"];

export type UserToDeployment = Database["public"]["Tables"]["users_to_deployments"]["Row"];
export type InsertUserToDeployment = Database["public"]["Tables"]["users_to_deployments"]["Insert"];
export type UpdateUserToDeployment = Database["public"]["Tables"]["users_to_deployments"]["Update"];

export type UserToRole = Database["public"]["Tables"]["users_to_roles"]["Row"];
export type InsertUserToRole = Database["public"]["Tables"]["users_to_roles"]["Insert"];
export type UpdateUserToRole = Database["public"]["Tables"]["users_to_roles"]["Update"];

export type UserToWorkspace = Database["public"]["Tables"]["users_to_workspaces"]["Row"];
export type InsertUserToWorkspace = Database["public"]["Tables"]["users_to_workspaces"]["Insert"];
export type UpdateUserToWorkspace = Database["public"]["Tables"]["users_to_workspaces"]["Update"];

export type WhitelabelBrand = Database["public"]["Tables"]["whitelabel_brands"]["Row"];
export type InsertWhitelabelBrand = Database["public"]["Tables"]["whitelabel_brands"]["Insert"];
export type UpdateWhitelabelBrand = Database["public"]["Tables"]["whitelabel_brands"]["Update"];

export type WorkspaceInvitation = Database["public"]["Tables"]["workspace_invitations"]["Row"];
export type InsertWorkspaceInvitation = Database["public"]["Tables"]["workspace_invitations"]["Insert"];
export type UpdateWorkspaceInvitation = Database["public"]["Tables"]["workspace_invitations"]["Update"];

export type WorkspaceProfileFile = Database["public"]["Tables"]["workspace_profile_files"]["Row"];
export type InsertWorkspaceProfileFile = Database["public"]["Tables"]["workspace_profile_files"]["Insert"];
export type UpdateWorkspaceProfileFile = Database["public"]["Tables"]["workspace_profile_files"]["Update"];

export type WorkspaceProfile = Database["public"]["Tables"]["workspace_profiles"]["Row"];
export type InsertWorkspaceProfile = Database["public"]["Tables"]["workspace_profiles"]["Insert"];
export type UpdateWorkspaceProfile = Database["public"]["Tables"]["workspace_profiles"]["Update"];

export type Workspace = Database["public"]["Tables"]["workspaces"]["Row"];
export type InsertWorkspace = Database["public"]["Tables"]["workspaces"]["Insert"];
export type UpdateWorkspace = Database["public"]["Tables"]["workspaces"]["Update"];

// Functions
export type ArgsAcceptDeploymentInvitation = Database["public"]["Functions"]["accept_deployment_invitation"]["Args"];
export type ReturnTypeAcceptDeploymentInvitation =
  Database["public"]["Functions"]["accept_deployment_invitation"]["Returns"];

export type ArgsAcceptWorkspaceInvitation = Database["public"]["Functions"]["accept_workspace_invitation"]["Args"];
export type ReturnTypeAcceptWorkspaceInvitation =
  Database["public"]["Functions"]["accept_workspace_invitation"]["Returns"];

export type ArgsAddCompressionPolicy = Database["public"]["Functions"]["add_compression_policy"]["Args"];
export type ReturnTypeAddCompressionPolicy = Database["public"]["Functions"]["add_compression_policy"]["Returns"];

export type ArgsAddContinuousAggregatePolicy =
  Database["public"]["Functions"]["add_continuous_aggregate_policy"]["Args"];
export type ReturnTypeAddContinuousAggregatePolicy =
  Database["public"]["Functions"]["add_continuous_aggregate_policy"]["Returns"];

export type ArgsAddDimension = Database["public"]["Functions"]["add_dimension"]["Args"];
export type ReturnTypeAddDimension = Database["public"]["Functions"]["add_dimension"]["Returns"];

export type ArgsAddJob = Database["public"]["Functions"]["add_job"]["Args"];
export type ReturnTypeAddJob = Database["public"]["Functions"]["add_job"]["Returns"];

export type ArgsAddPartToMaintenanceItem = Database["public"]["Functions"]["add_part_to_maintenance_item"]["Args"];
export type ReturnTypeAddPartToMaintenanceItem =
  Database["public"]["Functions"]["add_part_to_maintenance_item"]["Returns"];

export type ArgsAddReorderPolicy = Database["public"]["Functions"]["add_reorder_policy"]["Args"];
export type ReturnTypeAddReorderPolicy = Database["public"]["Functions"]["add_reorder_policy"]["Returns"];

export type ArgsAddRetentionPolicy = Database["public"]["Functions"]["add_retention_policy"]["Args"];
export type ReturnTypeAddRetentionPolicy = Database["public"]["Functions"]["add_retention_policy"]["Returns"];

export type ArgsAlterJob = Database["public"]["Functions"]["alter_job"]["Args"];
export type ReturnTypeAlterJob = Database["public"]["Functions"]["alter_job"]["Returns"];

export type ArgsApplySparePartUsage = Database["public"]["Functions"]["apply_spare_part_usage"]["Args"];
export type ReturnTypeApplySparePartUsage = Database["public"]["Functions"]["apply_spare_part_usage"]["Returns"];

export type ArgsApproximateRowCount = Database["public"]["Functions"]["approximate_row_count"]["Args"];
export type ReturnTypeApproximateRowCount = Database["public"]["Functions"]["approximate_row_count"]["Returns"];

export type ArgsAttachTablespace = Database["public"]["Functions"]["attach_tablespace"]["Args"];
export type ReturnTypeAttachTablespace = Database["public"]["Functions"]["attach_tablespace"]["Returns"];

export type ArgsByHash = Database["public"]["Functions"]["by_hash"]["Args"];
export type ReturnTypeByHash = Database["public"]["Functions"]["by_hash"]["Returns"];

export type ArgsByRange = Database["public"]["Functions"]["by_range"]["Args"];
export type ReturnTypeByRange = Database["public"]["Functions"]["by_range"]["Returns"];

export type ArgsChunkCompressionStat = Database["public"]["Functions"]["chunk_compression_stats"]["Args"];
export type ReturnTypeChunkCompressionStat = Database["public"]["Functions"]["chunk_compression_stats"]["Returns"];

export type ArgsChunkDetailedSize = Database["public"]["Functions"]["chunks_detailed_size"]["Args"];
export type ReturnTypeChunkDetailedSize = Database["public"]["Functions"]["chunks_detailed_size"]["Returns"];

export type ArgsCompressChunk = Database["public"]["Functions"]["compress_chunk"]["Args"];
export type ReturnTypeCompressChunk = Database["public"]["Functions"]["compress_chunk"]["Returns"];

export type ArgsCreateHypertable = Database["public"]["Functions"]["create_hypertable"]["Args"];
export type ReturnTypeCreateHypertable = Database["public"]["Functions"]["create_hypertable"]["Returns"];

export type ArgsCurrentUserHasDeploymentRole =
  Database["public"]["Functions"]["current_user_has_deployment_role"]["Args"];
export type ReturnTypeCurrentUserHasDeploymentRole =
  Database["public"]["Functions"]["current_user_has_deployment_role"]["Returns"];

export type ArgsCurrentUserHasGlobalRole = Database["public"]["Functions"]["current_user_has_global_role"]["Args"];
export type ReturnTypeCurrentUserHasGlobalRole =
  Database["public"]["Functions"]["current_user_has_global_role"]["Returns"];

export type ArgsCurrentUserHasWorkspaceRole =
  Database["public"]["Functions"]["current_user_has_workspace_role"]["Args"];
export type ReturnTypeCurrentUserHasWorkspaceRole =
  Database["public"]["Functions"]["current_user_has_workspace_role"]["Returns"];

export type ArgsCurrentUserOwnRow = Database["public"]["Functions"]["current_user_owns_row"]["Args"];
export type ReturnTypeCurrentUserOwnRow = Database["public"]["Functions"]["current_user_owns_row"]["Returns"];

export type ArgsCurrentUserOwnTemplate = Database["public"]["Functions"]["current_user_owns_template"]["Args"];
export type ReturnTypeCurrentUserOwnTemplate = Database["public"]["Functions"]["current_user_owns_template"]["Returns"];

export type ArgsDeclineDeploymentInvitation = Database["public"]["Functions"]["decline_deployment_invitation"]["Args"];
export type ReturnTypeDeclineDeploymentInvitation =
  Database["public"]["Functions"]["decline_deployment_invitation"]["Returns"];

export type ArgsDeclineWorkspaceInvitation = Database["public"]["Functions"]["decline_workspace_invitation"]["Args"];
export type ReturnTypeDeclineWorkspaceInvitation =
  Database["public"]["Functions"]["decline_workspace_invitation"]["Returns"];

export type ArgsDecompressChunk = Database["public"]["Functions"]["decompress_chunk"]["Args"];
export type ReturnTypeDecompressChunk = Database["public"]["Functions"]["decompress_chunk"]["Returns"];

export type ArgsDeleteDeployment = Database["public"]["Functions"]["delete_deployment"]["Args"];
export type ReturnTypeDeleteDeployment = Database["public"]["Functions"]["delete_deployment"]["Returns"];

export type ArgsDeleteJob = Database["public"]["Functions"]["delete_job"]["Args"];
export type ReturnTypeDeleteJob = Database["public"]["Functions"]["delete_job"]["Returns"];

export type ArgsDeleteWorkspace = Database["public"]["Functions"]["delete_workspace"]["Args"];
export type ReturnTypeDeleteWorkspace = Database["public"]["Functions"]["delete_workspace"]["Returns"];

export type ArgsDetachTablespace = Database["public"]["Functions"]["detach_tablespace"]["Args"];
export type ReturnTypeDetachTablespace = Database["public"]["Functions"]["detach_tablespace"]["Returns"];

export type ArgsDisableChunkSkipping = Database["public"]["Functions"]["disable_chunk_skipping"]["Args"];
export type ReturnTypeDisableChunkSkipping = Database["public"]["Functions"]["disable_chunk_skipping"]["Returns"];

export type ArgsDropChunk = Database["public"]["Functions"]["drop_chunks"]["Args"];
export type ReturnTypeDropChunk = Database["public"]["Functions"]["drop_chunks"]["Returns"];

export type ArgsEnableChunkSkipping = Database["public"]["Functions"]["enable_chunk_skipping"]["Args"];
export type ReturnTypeEnableChunkSkipping = Database["public"]["Functions"]["enable_chunk_skipping"]["Returns"];

export type ArgsExportCaseForCsv = Database["public"]["Functions"]["export_cases_for_csv"]["Args"];
export type ReturnTypeExportCaseForCsv = Database["public"]["Functions"]["export_cases_for_csv"]["Returns"];

export type ArgsExportFifoInventoryForCsv = Database["public"]["Functions"]["export_fifo_inventory_for_csv"]["Args"];
export type ReturnTypeExportFifoInventoryForCsv =
  Database["public"]["Functions"]["export_fifo_inventory_for_csv"]["Returns"];

export type ArgsExportMaintenanceItemForCsv =
  Database["public"]["Functions"]["export_maintenance_items_for_csv"]["Args"];
export type ReturnTypeExportMaintenanceItemForCsv =
  Database["public"]["Functions"]["export_maintenance_items_for_csv"]["Returns"];

export type ArgsExportMaintenanceForCsv = Database["public"]["Functions"]["export_maintenances_for_csv"]["Args"];
export type ReturnTypeExportMaintenanceForCsv =
  Database["public"]["Functions"]["export_maintenances_for_csv"]["Returns"];

export type ArgsFinishMaintenance = Database["public"]["Functions"]["finish_maintenance"]["Args"];
export type ReturnTypeFinishMaintenance = Database["public"]["Functions"]["finish_maintenance"]["Returns"];

export type ArgsGenerateWorkspaceAbbreviation =
  Database["public"]["Functions"]["generate_workspace_abbreviation"]["Args"];
export type ReturnTypeGenerateWorkspaceAbbreviation =
  Database["public"]["Functions"]["generate_workspace_abbreviation"]["Returns"];

export type ArgsGetBucketedDeviceScalarValue =
  Database["public"]["Functions"]["get_bucketed_device_scalar_values"]["Args"];
export type ReturnTypeGetBucketedDeviceScalarValue =
  Database["public"]["Functions"]["get_bucketed_device_scalar_values"]["Returns"];

export type ArgsGetCaseLog = Database["public"]["Functions"]["get_case_logs"]["Args"];
export type ReturnTypeGetCaseLog = Database["public"]["Functions"]["get_case_logs"]["Returns"];

export type ArgsGetDeviceLog = Database["public"]["Functions"]["get_device_logs"]["Args"];
export type ReturnTypeGetDeviceLog = Database["public"]["Functions"]["get_device_logs"]["Returns"];

export type ArgsGetDeviceLogByPart = Database["public"]["Functions"]["get_device_logs_by_part"]["Args"];
export type ReturnTypeGetDeviceLogByPart = Database["public"]["Functions"]["get_device_logs_by_part"]["Returns"];

export type ArgsGetLastHistory = Database["public"]["Functions"]["get_last_history"]["Args"];
export type ReturnTypeGetLastHistory = Database["public"]["Functions"]["get_last_history"]["Returns"];

export type ArgsGetMaintenanceLog = Database["public"]["Functions"]["get_maintenance_logs"]["Args"];
export type ReturnTypeGetMaintenanceLog = Database["public"]["Functions"]["get_maintenance_logs"]["Returns"];

export type ArgsGetTelemetryReport = Database["public"]["Functions"]["get_telemetry_report"]["Args"];
export type ReturnTypeGetTelemetryReport = Database["public"]["Functions"]["get_telemetry_report"]["Returns"];

export type ArgsHandleSinglePart = Database["public"]["Functions"]["handle_single_part"]["Args"];
export type ReturnTypeHandleSinglePart = Database["public"]["Functions"]["handle_single_part"]["Returns"];

export type ArgsHybridSearch = Database["public"]["Functions"]["hybrid_search"]["Args"];
export type ReturnTypeHybridSearch = Database["public"]["Functions"]["hybrid_search"]["Returns"];

export type ArgsHypertableApproximateDetailedSize =
  Database["public"]["Functions"]["hypertable_approximate_detailed_size"]["Args"];
export type ReturnTypeHypertableApproximateDetailedSize =
  Database["public"]["Functions"]["hypertable_approximate_detailed_size"]["Returns"];

export type ArgsHypertableApproximateSize = Database["public"]["Functions"]["hypertable_approximate_size"]["Args"];
export type ReturnTypeHypertableApproximateSize =
  Database["public"]["Functions"]["hypertable_approximate_size"]["Returns"];

export type ArgsHypertableCompressionStat = Database["public"]["Functions"]["hypertable_compression_stats"]["Args"];
export type ReturnTypeHypertableCompressionStat =
  Database["public"]["Functions"]["hypertable_compression_stats"]["Returns"];

export type ArgsHypertableDetailedSize = Database["public"]["Functions"]["hypertable_detailed_size"]["Args"];
export type ReturnTypeHypertableDetailedSize = Database["public"]["Functions"]["hypertable_detailed_size"]["Returns"];

export type ArgsHypertableIndexSize = Database["public"]["Functions"]["hypertable_index_size"]["Args"];
export type ReturnTypeHypertableIndexSize = Database["public"]["Functions"]["hypertable_index_size"]["Returns"];

export type ArgsHypertableSize = Database["public"]["Functions"]["hypertable_size"]["Args"];
export type ReturnTypeHypertableSize = Database["public"]["Functions"]["hypertable_size"]["Returns"];

export type ArgsInterpolate = Database["public"]["Functions"]["interpolate"]["Args"];
export type ReturnTypeInterpolate = Database["public"]["Functions"]["interpolate"]["Returns"];

export type ArgsInventoryAdd = Database["public"]["Functions"]["inventory_add"]["Args"];
export type ReturnTypeInventoryAdd = Database["public"]["Functions"]["inventory_add"]["Returns"];

export type ArgsInventoryAssemble = Database["public"]["Functions"]["inventory_assemble"]["Args"];
export type ReturnTypeInventoryAssemble = Database["public"]["Functions"]["inventory_assemble"]["Returns"];

export type ArgsInventoryAssembleInternal = Database["public"]["Functions"]["inventory_assemble_internal"]["Args"];
export type ReturnTypeInventoryAssembleInternal =
  Database["public"]["Functions"]["inventory_assemble_internal"]["Returns"];

export type ArgsInventoryChange = Database["public"]["Functions"]["inventory_change"]["Args"];
export type ReturnTypeInventoryChange = Database["public"]["Functions"]["inventory_change"]["Returns"];

export type ArgsInventoryDisassemble = Database["public"]["Functions"]["inventory_disassemble"]["Args"];
export type ReturnTypeInventoryDisassemble = Database["public"]["Functions"]["inventory_disassemble"]["Returns"];

export type ArgsInventoryDisassembleInternal =
  Database["public"]["Functions"]["inventory_disassemble_internal"]["Args"];
export type ReturnTypeInventoryDisassembleInternal =
  Database["public"]["Functions"]["inventory_disassemble_internal"]["Returns"];

export type ArgsInventoryIsolate = Database["public"]["Functions"]["inventory_isolate"]["Args"];
export type ReturnTypeInventoryIsolate = Database["public"]["Functions"]["inventory_isolate"]["Returns"];

export type ArgsInventoryIsolateInternal = Database["public"]["Functions"]["inventory_isolate_internal"]["Args"];
export type ReturnTypeInventoryIsolateInternal =
  Database["public"]["Functions"]["inventory_isolate_internal"]["Returns"];

export type ArgsInventoryMove = Database["public"]["Functions"]["inventory_move"]["Args"];
export type ReturnTypeInventoryMove = Database["public"]["Functions"]["inventory_move"]["Returns"];

export type ArgsInventoryMoveInternal = Database["public"]["Functions"]["inventory_move_internal"]["Args"];
export type ReturnTypeInventoryMoveInternal = Database["public"]["Functions"]["inventory_move_internal"]["Returns"];

export type ArgsInventoryRemove = Database["public"]["Functions"]["inventory_remove"]["Args"];
export type ReturnTypeInventoryRemove = Database["public"]["Functions"]["inventory_remove"]["Returns"];

export type ArgsInventoryUndo = Database["public"]["Functions"]["inventory_undo"]["Args"];
export type ReturnTypeInventoryUndo = Database["public"]["Functions"]["inventory_undo"]["Returns"];

export type ArgsLocf = Database["public"]["Functions"]["locf"]["Args"];
export type ReturnTypeLocf = Database["public"]["Functions"]["locf"]["Returns"];

export type ArgsMaintenanceFromChecklistTemplate =
  Database["public"]["Functions"]["maintenance_from_checklist_template"]["Args"];
export type ReturnTypeMaintenanceFromChecklistTemplate =
  Database["public"]["Functions"]["maintenance_from_checklist_template"]["Returns"];

export type ArgsMaintenanceFromDevice = Database["public"]["Functions"]["maintenance_from_devices"]["Args"];
export type ReturnTypeMaintenanceFromDevice = Database["public"]["Functions"]["maintenance_from_devices"]["Returns"];

export type ArgsMaintenanceFromMaintenance = Database["public"]["Functions"]["maintenance_from_maintenance"]["Args"];
export type ReturnTypeMaintenanceFromMaintenance =
  Database["public"]["Functions"]["maintenance_from_maintenance"]["Returns"];

export type ArgsMaintenanceFromMaintenanceStrategy =
  Database["public"]["Functions"]["maintenance_from_maintenance_strategy"]["Args"];
export type ReturnTypeMaintenanceFromMaintenanceStrategy =
  Database["public"]["Functions"]["maintenance_from_maintenance_strategy"]["Returns"];

export type ArgsMoveChunk = Database["public"]["Functions"]["move_chunk"]["Args"];
export type ReturnTypeMoveChunk = Database["public"]["Functions"]["move_chunk"]["Returns"];

export type ArgsMoveDevice = Database["public"]["Functions"]["move_device"]["Args"];
export type ReturnTypeMoveDevice = Database["public"]["Functions"]["move_device"]["Returns"];

export type ArgsParentMaintenance = Database["public"]["Functions"]["parent_maintenance"]["Args"];
export type ReturnTypeParentMaintenance = Database["public"]["Functions"]["parent_maintenance"]["Returns"];

export type ArgsRandomBetween = Database["public"]["Functions"]["random_between"]["Args"];
export type ReturnTypeRandomBetween = Database["public"]["Functions"]["random_between"]["Returns"];

export type ArgsRemoveCompressionPolicy = Database["public"]["Functions"]["remove_compression_policy"]["Args"];
export type ReturnTypeRemoveCompressionPolicy = Database["public"]["Functions"]["remove_compression_policy"]["Returns"];

export type ArgsRemoveContinuousAggregatePolicy =
  Database["public"]["Functions"]["remove_continuous_aggregate_policy"]["Args"];
export type ReturnTypeRemoveContinuousAggregatePolicy =
  Database["public"]["Functions"]["remove_continuous_aggregate_policy"]["Returns"];

export type ArgsRemoveReorderPolicy = Database["public"]["Functions"]["remove_reorder_policy"]["Args"];
export type ReturnTypeRemoveReorderPolicy = Database["public"]["Functions"]["remove_reorder_policy"]["Returns"];

export type ArgsRemoveRetentionPolicy = Database["public"]["Functions"]["remove_retention_policy"]["Args"];
export type ReturnTypeRemoveRetentionPolicy = Database["public"]["Functions"]["remove_retention_policy"]["Returns"];

export type ArgsRemoveUserFromWorkspace = Database["public"]["Functions"]["remove_user_from_workspace"]["Args"];
export type ReturnTypeRemoveUserFromWorkspace =
  Database["public"]["Functions"]["remove_user_from_workspace"]["Returns"];

export type ArgsReorderChunk = Database["public"]["Functions"]["reorder_chunk"]["Args"];
export type ReturnTypeReorderChunk = Database["public"]["Functions"]["reorder_chunk"]["Returns"];

export type ArgsReportNerror = Database["public"]["Functions"]["report_nerrors"]["Args"];
export type ReturnTypeReportNerror = Database["public"]["Functions"]["report_nerrors"]["Returns"];

export type ArgsReportNmission = Database["public"]["Functions"]["report_nmissions"]["Args"];
export type ReturnTypeReportNmission = Database["public"]["Functions"]["report_nmissions"]["Returns"];

export type ArgsRevertPart = Database["public"]["Functions"]["revert_part"]["Args"];
export type ReturnTypeRevertPart = Database["public"]["Functions"]["revert_part"]["Returns"];

export type ArgsRevokeDeploymentInvitation = Database["public"]["Functions"]["revoke_deployment_invitation"]["Args"];
export type ReturnTypeRevokeDeploymentInvitation =
  Database["public"]["Functions"]["revoke_deployment_invitation"]["Returns"];

export type ArgsSetAdaptiveChunking = Database["public"]["Functions"]["set_adaptive_chunking"]["Args"];
export type ReturnTypeSetAdaptiveChunking = Database["public"]["Functions"]["set_adaptive_chunking"]["Returns"];

export type ArgsSetChunkTimeInterval = Database["public"]["Functions"]["set_chunk_time_interval"]["Args"];
export type ReturnTypeSetChunkTimeInterval = Database["public"]["Functions"]["set_chunk_time_interval"]["Returns"];

export type ArgsSetIntegerNowFunc = Database["public"]["Functions"]["set_integer_now_func"]["Args"];
export type ReturnTypeSetIntegerNowFunc = Database["public"]["Functions"]["set_integer_now_func"]["Returns"];

export type ArgsSetNumberPartition = Database["public"]["Functions"]["set_number_partitions"]["Args"];
export type ReturnTypeSetNumberPartition = Database["public"]["Functions"]["set_number_partitions"]["Returns"];

export type ArgsSetPartitioningInterval = Database["public"]["Functions"]["set_partitioning_interval"]["Args"];
export type ReturnTypeSetPartitioningInterval = Database["public"]["Functions"]["set_partitioning_interval"]["Returns"];

export type ArgsShowChunk = Database["public"]["Functions"]["show_chunks"]["Args"];
export type ReturnTypeShowChunk = Database["public"]["Functions"]["show_chunks"]["Returns"];

export type ArgsShowTablespace = Database["public"]["Functions"]["show_tablespaces"]["Args"];
export type ReturnTypeShowTablespace = Database["public"]["Functions"]["show_tablespaces"]["Returns"];

export type ArgsSkipMaintenance = Database["public"]["Functions"]["skip_maintenance"]["Args"];
export type ReturnTypeSkipMaintenance = Database["public"]["Functions"]["skip_maintenance"]["Returns"];

export type ArgsSoftDeleteDevice = Database["public"]["Functions"]["soft_delete_device"]["Args"];
export type ReturnTypeSoftDeleteDevice = Database["public"]["Functions"]["soft_delete_device"]["Returns"];

export type ArgsStartMaintenance = Database["public"]["Functions"]["start_maintenance"]["Args"];
export type ReturnTypeStartMaintenance = Database["public"]["Functions"]["start_maintenance"]["Returns"];

export type ArgsTimeBucket = Database["public"]["Functions"]["time_bucket"]["Args"];
export type ReturnTypeTimeBucket = Database["public"]["Functions"]["time_bucket"]["Returns"];

export type ArgsTimeBucketGapfill = Database["public"]["Functions"]["time_bucket_gapfill"]["Args"];
export type ReturnTypeTimeBucketGapfill = Database["public"]["Functions"]["time_bucket_gapfill"]["Returns"];

export type ArgsTimescaledbPostRestore = Database["public"]["Functions"]["timescaledb_post_restore"]["Args"];
export type ReturnTypeTimescaledbPostRestore = Database["public"]["Functions"]["timescaledb_post_restore"]["Returns"];

export type ArgsTimescaledbPreRestore = Database["public"]["Functions"]["timescaledb_pre_restore"]["Args"];
export type ReturnTypeTimescaledbPreRestore = Database["public"]["Functions"]["timescaledb_pre_restore"]["Returns"];

export type ArgsTrackPartMovement = Database["public"]["Functions"]["track_part_movement"]["Args"];
export type ReturnTypeTrackPartMovement = Database["public"]["Functions"]["track_part_movement"]["Returns"];

export type ArgsTrackPartOperation = Database["public"]["Functions"]["track_part_operation"]["Args"];
export type ReturnTypeTrackPartOperation = Database["public"]["Functions"]["track_part_operation"]["Returns"];

export type ArgsUpdatePartQuantity = Database["public"]["Functions"]["update_part_quantity"]["Args"];
export type ReturnTypeUpdatePartQuantity = Database["public"]["Functions"]["update_part_quantity"]["Returns"];

export type ArgsWorkspaceIsServiceProviderOrManufacturer =
  Database["public"]["Functions"]["workspace_is_service_provider_or_manufacturer"]["Args"];
export type ReturnTypeWorkspaceIsServiceProviderOrManufacturer =
  Database["public"]["Functions"]["workspace_is_service_provider_or_manufacturer"]["Returns"];

// Schema: storage
// Tables
export type Bucket = Database["storage"]["Tables"]["buckets"]["Row"];
export type InsertBucket = Database["storage"]["Tables"]["buckets"]["Insert"];
export type UpdateBucket = Database["storage"]["Tables"]["buckets"]["Update"];

export type Migration = Database["storage"]["Tables"]["migrations"]["Row"];
export type InsertMigration = Database["storage"]["Tables"]["migrations"]["Insert"];
export type UpdateMigration = Database["storage"]["Tables"]["migrations"]["Update"];

export type Object = Database["storage"]["Tables"]["objects"]["Row"];
export type InsertObject = Database["storage"]["Tables"]["objects"]["Insert"];
export type UpdateObject = Database["storage"]["Tables"]["objects"]["Update"];

export type Prefix = Database["storage"]["Tables"]["prefixes"]["Row"];
export type InsertPrefix = Database["storage"]["Tables"]["prefixes"]["Insert"];
export type UpdatePrefix = Database["storage"]["Tables"]["prefixes"]["Update"];

export type S3MultipartUpload = Database["storage"]["Tables"]["s3_multipart_uploads"]["Row"];
export type InsertS3MultipartUpload = Database["storage"]["Tables"]["s3_multipart_uploads"]["Insert"];
export type UpdateS3MultipartUpload = Database["storage"]["Tables"]["s3_multipart_uploads"]["Update"];

export type S3MultipartUploadPart = Database["storage"]["Tables"]["s3_multipart_uploads_parts"]["Row"];
export type InsertS3MultipartUploadPart = Database["storage"]["Tables"]["s3_multipart_uploads_parts"]["Insert"];
export type UpdateS3MultipartUploadPart = Database["storage"]["Tables"]["s3_multipart_uploads_parts"]["Update"];

// Functions
export type ArgsAddPrefix = Database["storage"]["Functions"]["add_prefixes"]["Args"];
export type ReturnTypeAddPrefix = Database["storage"]["Functions"]["add_prefixes"]["Returns"];

export type ArgsCanInsertObject = Database["storage"]["Functions"]["can_insert_object"]["Args"];
export type ReturnTypeCanInsertObject = Database["storage"]["Functions"]["can_insert_object"]["Returns"];

export type ArgsDeletePrefix = Database["storage"]["Functions"]["delete_prefix"]["Args"];
export type ReturnTypeDeletePrefix = Database["storage"]["Functions"]["delete_prefix"]["Returns"];

export type ArgsExtension = Database["storage"]["Functions"]["extension"]["Args"];
export type ReturnTypeExtension = Database["storage"]["Functions"]["extension"]["Returns"];

export type ArgsFilename = Database["storage"]["Functions"]["filename"]["Args"];
export type ReturnTypeFilename = Database["storage"]["Functions"]["filename"]["Returns"];

export type ArgsFoldername = Database["storage"]["Functions"]["foldername"]["Args"];
export type ReturnTypeFoldername = Database["storage"]["Functions"]["foldername"]["Returns"];

export type ArgsGetLevel = Database["storage"]["Functions"]["get_level"]["Args"];
export type ReturnTypeGetLevel = Database["storage"]["Functions"]["get_level"]["Returns"];

export type ArgsGetPrefix = Database["storage"]["Functions"]["get_prefixes"]["Args"];
export type ReturnTypeGetPrefix = Database["storage"]["Functions"]["get_prefixes"]["Returns"];

export type ArgsGetSizeByBucket = Database["storage"]["Functions"]["get_size_by_bucket"]["Args"];
export type ReturnTypeGetSizeByBucket = Database["storage"]["Functions"]["get_size_by_bucket"]["Returns"];

export type ArgsListMultipartUploadWithDelimiter =
  Database["storage"]["Functions"]["list_multipart_uploads_with_delimiter"]["Args"];
export type ReturnTypeListMultipartUploadWithDelimiter =
  Database["storage"]["Functions"]["list_multipart_uploads_with_delimiter"]["Returns"];

export type ArgsListObjectWithDelimiter = Database["storage"]["Functions"]["list_objects_with_delimiter"]["Args"];
export type ReturnTypeListObjectWithDelimiter =
  Database["storage"]["Functions"]["list_objects_with_delimiter"]["Returns"];

export type ArgsOperation = Database["storage"]["Functions"]["operation"]["Args"];
export type ReturnTypeOperation = Database["storage"]["Functions"]["operation"]["Returns"];

export type ArgsSearch = Database["storage"]["Functions"]["search"]["Args"];
export type ReturnTypeSearch = Database["storage"]["Functions"]["search"]["Returns"];

export type ArgsSearchLegacyV1 = Database["storage"]["Functions"]["search_legacy_v1"]["Args"];
export type ReturnTypeSearchLegacyV1 = Database["storage"]["Functions"]["search_legacy_v1"]["Returns"];

export type ArgsSearchV1Optimised = Database["storage"]["Functions"]["search_v1_optimised"]["Args"];
export type ReturnTypeSearchV1Optimised = Database["storage"]["Functions"]["search_v1_optimised"]["Returns"];

export type ArgsSearchV2 = Database["storage"]["Functions"]["search_v2"]["Args"];
export type ReturnTypeSearchV2 = Database["storage"]["Functions"]["search_v2"]["Returns"];
